import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Shield, LayoutDashboard, Users, Stethoscope, FileText,
  CreditCard, Settings, LogOut, Bell, Menu,
  TrendingUp, TrendingDown, Calendar, Euro, UserPlus,
  MoreVertical, Check, XCircle, Eye, Edit, Trash2,
  ChevronRight, Download, Plus, RefreshCw,
  HelpCircle, Star, Mail, Phone, Lock,
  MapPin, Facebook, Instagram, Twitter, Linkedin, Save,
  Megaphone, ShoppingBag, ExternalLink, Image, Link as LinkIcon,
  PanelTop, PanelBottom, Navigation, GripVertical,
  Home, Palette, Share2, Contact, Clock, Youtube, Globe, Search,
  Scale, FileCheck, CheckCircle, XOctagon, BookOpen, Gift, BarChart3, Rocket, Moon
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useArticles } from '../../context/ArticlesContext';
import { useFAQ } from '../../context/FAQContext';
import { useReviews } from '../../context/ReviewsContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useAds } from '../../context/AdsContext';
import AdminContentManager from '../../components/admin/AdminContentManager';
import { useProducts } from '../../context/ProductsContext';
import ArticleEditor from '../../components/admin/ArticleEditor';
import AdminAnalyticsDashboard from '../../components/admin/AdminAnalyticsDashboard';
import AdminVeterinairesContent from './AdminVeterinairesContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mock data (will be replaced by API calls)
const mockVeterinaires = [
  { id: 1, name: 'Clinique Vétérinaire du Parc', email: 'contact@clinique-parc.fr', city: 'Paris', status: 'active', subscription: 'active', revenue: 350 },
  { id: 2, name: 'Vétérinaires Associés Saint-Michel', email: 'contact@veto-sm.fr', city: 'Paris', status: 'active', subscription: 'active', revenue: 350 },
  { id: 3, name: 'Centre Vétérinaire de Lyon', email: 'contact@veto-lyon.fr', city: 'Lyon', status: 'pending', subscription: 'trial', revenue: 0 },
];

const mockPatients = [
  { id: 1, name: 'Marie Dupont', email: 'marie.dupont@email.fr', animals: 2, appointments: 5 },
  { id: 2, name: 'Pierre Martin', email: 'p.martin@email.fr', animals: 1, appointments: 3 },
];

const mockPayments = [
  { id: 1, vet: 'Clinique Vétérinaire du Parc', amount: 35, date: '2024-12-01', status: 'paid', method: 'Stripe' },
  { id: 2, vet: 'Vétérinaires Associés Saint-Michel', amount: 35, date: '2024-12-01', status: 'paid', method: 'PayPal' },
];

const StatCard = ({ title, value, icon: Icon, color = 'green' }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <div className={`w-12 h-12 bg-${color}-100 rounded-xl flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
    </div>
    <p className="text-3xl font-bold text-gray-800">{value}</p>
    <p className="text-sm text-gray-500 mt-1">{title}</p>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Context hooks
  const { articles, addArticle, updateArticle, deleteArticle, resetToDefault: resetArticles } = useArticles();
  const { faqs, addFAQ, updateFAQ, deleteFAQ, toggleFAQActive, resetToDefault: resetFAQs } = useFAQ();
  const { reviews, approveReview, rejectReview, deleteReview, getStats: getReviewStats } = useReviews();
  const { settings, updateSettings } = useSiteSettings();
  const { ads, addAd, updateAd, deleteAd, toggleAdActive, getStats: getAdsStats, resetToDefault: resetAds } = useAds();
  const { products, addProduct, updateProduct, deleteProduct, toggleProductActive, toggleProductFeatured, categories, getStats: getProductsStats, resetToDefault: resetProducts } = useProducts();
  
  // Modal states
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  
  // FAQ editor
  const [isFAQEditorOpen, setIsFAQEditorOpen] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '' });

  // Ad editor
  const [isAdEditorOpen, setIsAdEditorOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adForm, setAdForm] = useState({
    name: '', type: 'banner', position: 'header', imageUrl: '', linkUrl: '',
    altText: '', advertiser: '', price: 0, startDate: '', endDate: '', active: true
  });

  // Product editor
  const [isProductEditorOpen, setIsProductEditorOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: 0, originalPrice: 0, image: '',
    category: 'accessoires', animal: 'tous', affiliateUrl: '', brand: '', featured: false, active: true
  });

  // Settings
  const [settingsForm, setSettingsForm] = useState(settings);

  // Veterinarians & Patients from API
  const [veterinarians, setVeterinarians] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loadingVets, setLoadingVets] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // Header/Footer settings
  const [headerForm, setHeaderForm] = useState(settings.header || {});
  const [footerForm, setFooterForm] = useState(settings.footer || {});
  const [editingNavLink, setEditingNavLink] = useState(null);
  const [navLinkForm, setNavLinkForm] = useState({ label: '', path: '', icon: 'none', active: true });
  const [isNavLinkEditorOpen, setIsNavLinkEditorOpen] = useState(false);
  const [editingFooterSection, setEditingFooterSection] = useState(null);
  const [footerSectionForm, setFooterSectionForm] = useState({ title: '', links: [] });
  const [isFooterSectionEditorOpen, setIsFooterSectionEditorOpen] = useState(false);
  const [editingFooterLink, setEditingFooterLink] = useState(null);
  const [footerLinkForm, setFooterLinkForm] = useState({ label: '', path: '', isExternal: false });
  const [isFooterLinkEditorOpen, setIsFooterLinkEditorOpen] = useState(false);

  // Homepage settings
  const [homepageForm, setHomepageForm] = useState(settings.homepage || {});
  
  // Theme settings
  const [themeForm, setThemeForm] = useState(settings.theme || {});
  
  // Social settings
  const [socialForm, setSocialForm] = useState(settings.social || {});
  
  // Contact settings
  const [contactForm, setContactForm] = useState(settings.contact || {});

  // Legal settings
  const [legalForm, setLegalForm] = useState(settings.legal || {});
  const [activeLegalTab, setActiveLegalTab] = useState('mentionsLegales');

  // Pricing settings
  const [pricingForm, setPricingForm] = useState(settings.pricing || {});

  // Password change
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Homepage content management
  const [homepageContent, setHomepageContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // Profile detail modal
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileType, setProfileType] = useState(null); // 'vet' or 'patient'
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Founder Program Cities
  const [founderCities, setFounderCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [isCityEditorOpen, setIsCityEditorOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [cityForm, setCityForm] = useState({
    name: '',
    total_spots: 10,
    remaining_spots: 10,
    is_active: true,
    description: '',
    launch_date: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) navigate('/admin');
  }, [navigate]);

  // Fetch veterinarians
  const fetchVeterinarians = async () => {
    setLoadingVets(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/veterinarians`, {
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (response.ok) {
        const data = await response.json();
        setVeterinarians(data);
      }
    } catch (error) {
      console.error('Error fetching vets:', error);
    }
    setLoadingVets(false);
  };

  // Fetch patients
  const fetchPatients = async () => {
    setLoadingPatients(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/patients`, {
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (response.ok) {
        const data = await response.json();
        setPatients(data);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
    setLoadingPatients(false);
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.is_read).length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/notifications/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      fetchNotifications();
      toast.success('Toutes les notifications marquées comme lues');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Fetch founder cities
  const fetchFounderCities = async () => {
    setLoadingCities(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/founder-program/cities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFounderCities(data);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
    setLoadingCities(false);
  };

  // Create/Update city
  const handleSaveCity = async () => {
    if (!cityForm.name.trim()) {
      toast.error('Le nom de la ville est requis');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const url = editingCity 
        ? `${API_URL}/api/founder-program/cities/${editingCity.city_id}`
        : `${API_URL}/api/founder-program/cities`;
      
      const response = await fetch(url, {
        method: editingCity ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cityForm)
      });
      
      if (response.ok) {
        toast.success(editingCity ? 'Ville mise à jour' : 'Ville créée');
        setIsCityEditorOpen(false);
        setEditingCity(null);
        setCityForm({
          name: '',
          total_spots: 10,
          remaining_spots: 10,
          is_active: true,
          description: '',
          launch_date: ''
        });
        fetchFounderCities();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Delete city
  const handleDeleteCity = async (cityId) => {
    if (!window.confirm('Supprimer cette ville ?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/founder-program/cities/${cityId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Ville supprimée');
        fetchFounderCities();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Fetch homepage content
  const fetchHomepageContent = async () => {
    setLoadingContent(true);
    try {
      const response = await fetch(`${API_URL}/api/content/homepage`);
      if (response.ok) {
        const data = await response.json();
        setHomepageContent(data);
      }
    } catch (error) {
      console.error('Error fetching homepage content:', error);
    }
    setLoadingContent(false);
  };

  // Save homepage content
  const saveHomepageContent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
        return;
      }
      
      const response = await fetch(`${API_URL}/api/content/homepage`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(homepageContent)
      });
      
      if (response.ok) {
        toast.success('Contenu sauvegardé !');
        fetchHomepageContent();
      } else if (response.status === 401) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
        localStorage.removeItem('adminLoggedIn');
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur de connexion');
    }
  };

  // Update feature
  const updateFeature = (featureId, field, value) => {
    setHomepageContent(prev => ({
      ...prev,
      features: prev.features.map(f => 
        f.id === featureId ? { ...f, [field]: value } : f
      )
    }));
  };

  // View profile details
  const viewProfileDetails = async (userId, type) => {
    setLoadingProfile(true);
    setProfileType(type);
    setShowProfileModal(true);
    
    try {
      const token = localStorage.getItem('adminToken');
      const endpoint = type === 'vet' 
        ? `${API_URL}/api/admin/veterinarians/${userId}/details`
        : `${API_URL}/api/admin/patients/${userId}/details`;
      
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedProfile(data);
      } else {
        toast.error('Erreur lors du chargement du profil');
        setShowProfileModal(false);
      }
    } catch (error) {
      toast.error('Erreur de connexion');
      setShowProfileModal(false);
    }
    setLoadingProfile(false);
  };

  // Approve veterinarian
  const approveVeterinarian = async (userId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/veterinarians/${userId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (response.ok) {
        toast.success('Vétérinaire approuvé');
        fetchVeterinarians();
      } else {
        toast.error('Erreur lors de l\'approbation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Reject veterinarian
  const rejectVeterinarian = async (userId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/veterinarians/${userId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (response.ok) {
        toast.success('Vétérinaire refusé');
        fetchVeterinarians();
      } else {
        toast.error('Erreur lors du refus');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Approve patient
  const approvePatient = async (userId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/patients/${userId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (response.ok) {
        toast.success('Patient approuvé');
        fetchPatients();
      } else {
        toast.error('Erreur lors de l\'approbation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Reject patient
  const rejectPatient = async (userId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/patients/${userId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (response.ok) {
        toast.success('Patient refusé');
        fetchPatients();
      } else {
        toast.error('Erreur lors du refus');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Delete veterinarian
  const deleteVeterinarian = async (userId, name) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le compte de "${name}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/veterinarians/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success('Compte vétérinaire supprimé');
        fetchVeterinarians();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Delete patient
  const deletePatient = async (userId, name) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le compte de "${name}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/patients/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success('Compte patient supprimé');
        fetchPatients();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Fetch data on tab change
  useEffect(() => {
    if (activeTab === 'veterinaires') {
      fetchVeterinarians();
    } else if (activeTab === 'patients') {
      fetchPatients();
    } else if (activeTab === 'contenu') {
      fetchHomepageContent();
    } else if (activeTab === 'founder-program') {
      fetchFounderCities();
    }
  }, [activeTab]);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { setSettingsForm(settings); }, [settings]);
  useEffect(() => { setHeaderForm(settings.header || {}); }, [settings.header]);
  useEffect(() => { setFooterForm(settings.footer || {}); }, [settings.footer]);
  useEffect(() => { setHomepageForm(settings.homepage || {}); }, [settings.homepage]);
  useEffect(() => { setThemeForm(settings.theme || {}); }, [settings.theme]);
  useEffect(() => { setSocialForm(settings.social || {}); }, [settings.social]);
  useEffect(() => { setContactForm(settings.contact || {}); }, [settings.contact]);
  useEffect(() => { setLegalForm(settings.legal || {}); }, [settings.legal]);
  useEffect(() => { setPricingForm(settings.pricing || {}); }, [settings.pricing]);

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    toast.success('Déconnexion réussie');
    navigate('/admin');
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    
    setIsChangingPassword(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        
        body: JSON.stringify({
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword,
          confirm_password: passwordForm.confirmPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Mot de passe modifié avec succès');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        toast.error(data.detail || 'Erreur lors du changement de mot de passe');
      }
    } catch (err) {
      toast.error('Erreur de connexion au serveur');
    }
    setIsChangingPassword(false);
  };

  // Article handlers
  const handleSaveArticle = (formData) => {
    if (editingArticle) {
      updateArticle(editingArticle.id, formData);
      toast.success('Article modifié');
    } else {
      addArticle(formData);
      toast.success('Article créé');
    }
    setIsEditorOpen(false);
    setEditingArticle(null);
  };

  // FAQ handlers
  const handleSaveFAQ = () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) {
      toast.error('Remplissez tous les champs');
      return;
    }
    if (editingFAQ) {
      updateFAQ(editingFAQ.id, faqForm);
      toast.success('FAQ modifiée');
    } else {
      addFAQ(faqForm);
      toast.success('FAQ créée');
    }
    setIsFAQEditorOpen(false);
    setEditingFAQ(null);
    setFaqForm({ question: '', answer: '' });
  };

  // Ad handlers
  const handleCreateAd = () => {
    setEditingAd(null);
    setAdForm({
      name: '', type: 'banner', position: 'article-middle', imageUrl: '', linkUrl: '',
      altText: '', advertiser: '', price: 0, startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 90*24*60*60*1000).toISOString().split('T')[0], active: true
    });
    setIsAdEditorOpen(true);
  };

  const handleEditAd = (ad) => {
    setEditingAd(ad);
    setAdForm({ ...ad });
    setIsAdEditorOpen(true);
  };

  const handleSaveAd = () => {
    if (!adForm.name || !adForm.imageUrl || !adForm.linkUrl) {
      toast.error('Remplissez les champs obligatoires');
      return;
    }
    if (editingAd) {
      updateAd(editingAd.id, adForm);
      toast.success('Publicité modifiée');
    } else {
      addAd(adForm);
      toast.success('Publicité créée');
    }
    setIsAdEditorOpen(false);
    setEditingAd(null);
  };

  // Product handlers
  const handleCreateProduct = () => {
    setEditingProduct(null);
    setProductForm({
      name: '', description: '', price: 0, originalPrice: 0, image: '',
      category: 'accessoires', animal: 'tous', affiliateUrl: '', brand: '', featured: false, active: true
    });
    setIsProductEditorOpen(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({ ...product });
    setIsProductEditorOpen(true);
  };

  const handleSaveProduct = () => {
    if (!productForm.name || !productForm.image || !productForm.affiliateUrl) {
      toast.error('Remplissez les champs obligatoires');
      return;
    }
    if (editingProduct) {
      updateProduct(editingProduct.id, productForm);
      toast.success('Produit modifié');
    } else {
      addProduct(productForm);
      toast.success('Produit créé');
    }
    setIsProductEditorOpen(false);
    setEditingProduct(null);
  };

  // Delete handlers
  const handleDeleteClick = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteType === 'article') deleteArticle(itemToDelete.id);
    else if (deleteType === 'faq') deleteFAQ(itemToDelete.id);
    else if (deleteType === 'review') deleteReview(itemToDelete.id);
    else if (deleteType === 'ad') deleteAd(itemToDelete.id);
    else if (deleteType === 'product') deleteProduct(itemToDelete.id);
    toast.success('Élément supprimé');
    setDeleteConfirmOpen(false);
  };

  const handleSaveSettings = () => {
    updateSettings(settingsForm);
    toast.success('Paramètres enregistrés');
  };

  // Header handlers
  const handleSaveHeader = () => {
    updateSettings({ header: headerForm });
    toast.success('Header enregistré');
  };

  const handleAddNavLink = () => {
    setEditingNavLink(null);
    setNavLinkForm({ label: '', path: '/', icon: 'none', active: true });
    setIsNavLinkEditorOpen(true);
  };

  const handleEditNavLink = (link) => {
    setEditingNavLink(link);
    setNavLinkForm({ ...link });
    setIsNavLinkEditorOpen(true);
  };

  const handleSaveNavLink = () => {
    if (!navLinkForm.label || !navLinkForm.path) {
      toast.error('Remplissez tous les champs');
      return;
    }
    const currentLinks = headerForm.navLinks || [];
    if (editingNavLink) {
      const updatedLinks = currentLinks.map(l => l.id === editingNavLink.id ? { ...navLinkForm, id: l.id } : l);
      setHeaderForm({ ...headerForm, navLinks: updatedLinks });
    } else {
      const newLink = { ...navLinkForm, id: Date.now().toString() };
      setHeaderForm({ ...headerForm, navLinks: [...currentLinks, newLink] });
    }
    setIsNavLinkEditorOpen(false);
    toast.success(editingNavLink ? 'Lien modifié' : 'Lien ajouté');
  };

  const handleDeleteNavLink = (linkId) => {
    const updatedLinks = (headerForm.navLinks || []).filter(l => l.id !== linkId);
    setHeaderForm({ ...headerForm, navLinks: updatedLinks });
    toast.success('Lien supprimé');
  };

  const handleToggleNavLink = (linkId) => {
    const updatedLinks = (headerForm.navLinks || []).map(l => 
      l.id === linkId ? { ...l, active: !l.active } : l
    );
    setHeaderForm({ ...headerForm, navLinks: updatedLinks });
  };

  // Footer handlers
  const handleSaveFooter = () => {
    updateSettings({ footer: footerForm });
    toast.success('Footer enregistré');
  };

  const handleAddFooterSection = () => {
    setEditingFooterSection(null);
    setFooterSectionForm({ title: '', links: [] });
    setIsFooterSectionEditorOpen(true);
  };

  const handleEditFooterSection = (section) => {
    setEditingFooterSection(section);
    setFooterSectionForm({ ...section });
    setIsFooterSectionEditorOpen(true);
  };

  const handleSaveFooterSection = () => {
    if (!footerSectionForm.title) {
      toast.error('Entrez un titre');
      return;
    }
    const currentSections = footerForm.sections || [];
    if (editingFooterSection) {
      const updatedSections = currentSections.map(s => 
        s.id === editingFooterSection.id ? { ...footerSectionForm, id: s.id } : s
      );
      setFooterForm({ ...footerForm, sections: updatedSections });
    } else {
      const newSection = { ...footerSectionForm, id: Date.now().toString(), links: [] };
      setFooterForm({ ...footerForm, sections: [...currentSections, newSection] });
    }
    setIsFooterSectionEditorOpen(false);
    toast.success(editingFooterSection ? 'Section modifiée' : 'Section ajoutée');
  };

  const handleDeleteFooterSection = (sectionId) => {
    const updatedSections = (footerForm.sections || []).filter(s => s.id !== sectionId);
    setFooterForm({ ...footerForm, sections: updatedSections });
    toast.success('Section supprimée');
  };

  const handleAddFooterLink = (sectionId) => {
    setEditingFooterLink({ sectionId, link: null });
    setFooterLinkForm({ label: '', path: '/', isExternal: false, isEmail: false, isPhone: false });
    setIsFooterLinkEditorOpen(true);
  };

  const handleEditFooterLink = (sectionId, link) => {
    setEditingFooterLink({ sectionId, link });
    setFooterLinkForm({ ...link });
    setIsFooterLinkEditorOpen(true);
  };

  const handleSaveFooterLink = () => {
    if (!footerLinkForm.label || !footerLinkForm.path) {
      toast.error('Remplissez tous les champs');
      return;
    }
    const sectionId = editingFooterLink.sectionId;
    const updatedSections = (footerForm.sections || []).map(s => {
      if (s.id !== sectionId) return s;
      if (editingFooterLink.link) {
        const updatedLinks = s.links.map(l => 
          l.id === editingFooterLink.link.id ? { ...footerLinkForm, id: l.id } : l
        );
        return { ...s, links: updatedLinks };
      } else {
        const newLink = { ...footerLinkForm, id: Date.now().toString() };
        return { ...s, links: [...(s.links || []), newLink] };
      }
    });
    setFooterForm({ ...footerForm, sections: updatedSections });
    setIsFooterLinkEditorOpen(false);
    toast.success(editingFooterLink.link ? 'Lien modifié' : 'Lien ajouté');
  };

  const handleDeleteFooterLink = (sectionId, linkId) => {
    const updatedSections = (footerForm.sections || []).map(s => {
      if (s.id !== sectionId) return s;
      return { ...s, links: s.links.filter(l => l.id !== linkId) };
    });
    setFooterForm({ ...footerForm, sections: updatedSections });
    toast.success('Lien supprimé');
  };

  // Homepage handlers
  const handleSaveHomepage = () => {
    updateSettings({ homepage: homepageForm });
    toast.success('Page d\'accueil enregistrée');
  };

  // Theme handlers
  const handleSaveTheme = () => {
    updateSettings({ theme: themeForm });
    // Also update header logo colors to match theme
    const updatedHeader = {
      ...headerForm,
      logoColor: themeForm.primaryColor,
      logoColorDark: themeForm.primaryColorDark
    };
    setHeaderForm(updatedHeader);
    updateSettings({ header: updatedHeader });
    toast.success('Thème enregistré');
  };

  // Social handlers
  const handleSaveSocial = () => {
    updateSettings({ social: socialForm });
    toast.success('Réseaux sociaux enregistrés');
  };

  // Contact handlers
  const handleSaveContact = () => {
    updateSettings({ contact: contactForm });
    toast.success('Page contact enregistrée');
  };

  const handleAddOpeningHour = () => {
    const currentHours = contactForm.openingHours || [];
    setContactForm({
      ...contactForm,
      openingHours: [...currentHours, { day: '', hours: '' }]
    });
  };

  const handleUpdateOpeningHour = (index, field, value) => {
    const updatedHours = [...(contactForm.openingHours || [])];
    updatedHours[index] = { ...updatedHours[index], [field]: value };
    setContactForm({ ...contactForm, openingHours: updatedHours });
  };

  const handleDeleteOpeningHour = (index) => {
    const updatedHours = (contactForm.openingHours || []).filter((_, i) => i !== index);
    setContactForm({ ...contactForm, openingHours: updatedHours });
  };

  // Legal handlers
  const handleSaveLegal = () => {
    updateSettings({ legal: legalForm });
    toast.success('Pages légales enregistrées');
  };

  const handleUpdateLegalSection = (pageKey, sectionIndex, field, value) => {
    const updatedPage = { ...legalForm[pageKey] };
    updatedPage.sections = [...(updatedPage.sections || [])];
    updatedPage.sections[sectionIndex] = { 
      ...updatedPage.sections[sectionIndex], 
      [field]: value 
    };
    setLegalForm({ ...legalForm, [pageKey]: updatedPage });
  };

  const handleAddLegalSection = (pageKey) => {
    const updatedPage = { ...legalForm[pageKey] };
    const sections = updatedPage.sections || [];
    const newSection = {
      id: Date.now().toString(),
      title: `${sections.length + 1}. Nouvelle section`,
      content: ''
    };
    updatedPage.sections = [...sections, newSection];
    setLegalForm({ ...legalForm, [pageKey]: updatedPage });
  };

  const handleDeleteLegalSection = (pageKey, sectionIndex) => {
    const updatedPage = { ...legalForm[pageKey] };
    updatedPage.sections = (updatedPage.sections || []).filter((_, i) => i !== sectionIndex);
    setLegalForm({ ...legalForm, [pageKey]: updatedPage });
    toast.success('Section supprimée');
  };

  const handleUpdateLegalMeta = (pageKey, field, value) => {
    const updatedPage = { ...legalForm[pageKey], [field]: value };
    setLegalForm({ ...legalForm, [pageKey]: updatedPage });
  };

  const menuItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'founder-program', label: 'Programme Fondateur', icon: Rocket },
    { id: 'veterinaires-content', label: 'Page Vétérinaires', icon: FileText },
    { id: 'promotion', label: 'Promotion', icon: Gift },
    { id: 'cms', label: 'Conseils & Avis', icon: BookOpen },
    { id: 'contenu', label: 'Contenu du site', icon: Edit },
    { id: 'homepage', label: 'Page d\'accueil', icon: Home },
    { id: 'pricing', label: 'Tarification', icon: CreditCard },
    { id: 'theme', label: 'Thème & Couleurs', icon: Palette },
    { id: 'social', label: 'Réseaux sociaux', icon: Share2 },
    { id: 'contact-page', label: 'Page Contact', icon: Contact },
    { id: 'header-footer', label: 'Header & Footer', icon: PanelTop },
    { id: 'legal', label: 'Pages légales', icon: Scale },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
    { id: 'ads', label: 'Publicités', icon: Megaphone },
    { id: 'products', label: 'Boutique', icon: ShoppingBag },
    { id: 'veterinaires', label: 'Vétérinaires', icon: Stethoscope },
    { id: 'patients', label: 'Patients', icon: Users },
    { id: 'payments', label: 'Paiements', icon: CreditCard },
    { id: 'settings', label: 'Paramètres', icon: Settings },
  ];

  const reviewStats = getReviewStats();
  const adsStats = getAdsStats();
  const productsStats = getProductsStats();

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard title="Articles" value={articles.length} icon={FileText} />
              <StatCard title="Publicités actives" value={adsStats.active} icon={Megaphone} color="blue" />
              <StatCard title="Produits" value={productsStats.active} icon={ShoppingBag} color="purple" />
              <StatCard title="Avis en attente" value={reviewStats.pending} icon={Star} color="yellow" />
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-gray-800 mb-4">Revenus Publicités</h3>
                <p className="text-3xl font-bold text-green-600">{adsStats.totalRevenue}€</p>
                <p className="text-sm text-gray-500 mt-1">{adsStats.totalClicks} clics • {adsStats.avgCTR}% CTR</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-gray-800 mb-4">Résumé</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Vétérinaires</span><span className="font-medium">{mockVeterinaires.length}</span></div>
                  <div className="flex justify-between"><span>FAQ actives</span><span className="font-medium">{faqs.filter(f => f.active).length}</span></div>
                  <div className="flex justify-between"><span>Produits vedettes</span><span className="font-medium">{productsStats.featured}</span></div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'analytics':
        return <AdminAnalyticsDashboard />;

      case 'founder-program':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Rocket className="w-6 h-6 text-blue-500" />
                  <h2 className="text-xl font-semibold">Programme Clinique Fondatrice</h2>
                </div>
                <Button
                  onClick={() => {
                    setEditingCity(null);
                    setCityForm({
                      name: '',
                      total_spots: 10,
                      remaining_spots: 10,
                      is_active: true,
                      description: '',
                      launch_date: ''
                    });
                    setIsCityEditorOpen(true);
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" /> Ajouter une ville
                </Button>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                Gérez les villes du programme fondateur. Vous pouvez ajouter de nouvelles villes, 
                modifier le nombre de places disponibles et activer/désactiver des villes.
              </p>

              {loadingCities ? (
                <div className="text-center py-8 text-gray-500">Chargement...</div>
              ) : founderCities.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Aucune ville configurée</p>
                  <p className="text-sm text-gray-400">Ajoutez votre première ville pour démarrer le programme</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {founderCities.map((city) => (
                    <div 
                      key={city.city_id}
                      className={`border rounded-xl p-4 ${city.is_active ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${city.is_active ? 'bg-green-100' : 'bg-gray-200'}`}>
                            <MapPin className={`w-5 h-5 ${city.is_active ? 'text-green-600' : 'text-gray-500'}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{city.name}</h3>
                            <p className="text-sm text-gray-500">
                              {city.remaining_spots} places restantes sur {city.total_spots}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            city.is_active ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {city.is_active ? 'Active' : 'Inactive'}
                          </span>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setEditingCity(city);
                                setCityForm({
                                  name: city.name,
                                  total_spots: city.total_spots,
                                  remaining_spots: city.remaining_spots,
                                  is_active: city.is_active,
                                  description: city.description || '',
                                  launch_date: city.launch_date || ''
                                });
                                setIsCityEditorOpen(true);
                              }}>
                                <Edit className="w-4 h-4 mr-2" /> Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteCity(city.city_id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Places utilisées</span>
                          <span>{city.total_spots - city.remaining_spots}/{city.total_spots}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${city.remaining_spots > 0 ? 'bg-blue-500' : 'bg-red-500'}`}
                            style={{ width: `${((city.total_spots - city.remaining_spots) / city.total_spots) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      {city.description && (
                        <p className="mt-3 text-sm text-gray-600">{city.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* City Editor Dialog */}
            <Dialog open={isCityEditorOpen} onOpenChange={setIsCityEditorOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    {editingCity ? 'Modifier la ville' : 'Ajouter une ville'}
                  </DialogTitle>
                  <DialogDescription>
                    Configurez les paramètres de la ville pour le programme fondateur
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="city-name">Nom de la ville *</Label>
                    <Input
                      id="city-name"
                      value={cityForm.name}
                      onChange={(e) => setCityForm({...cityForm, name: e.target.value})}
                      placeholder="Ex: Nîmes, Lyon, Paris..."
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="total-spots">Places totales</Label>
                      <Input
                        id="total-spots"
                        type="number"
                        min="1"
                        value={cityForm.total_spots}
                        onChange={(e) => setCityForm({...cityForm, total_spots: parseInt(e.target.value) || 1})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="remaining-spots">Places restantes</Label>
                      <Input
                        id="remaining-spots"
                        type="number"
                        min="0"
                        max={cityForm.total_spots}
                        value={cityForm.remaining_spots}
                        onChange={(e) => setCityForm({...cityForm, remaining_spots: parseInt(e.target.value) || 0})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="city-description">Description (optionnel)</Label>
                    <Textarea
                      id="city-description"
                      value={cityForm.description}
                      onChange={(e) => setCityForm({...cityForm, description: e.target.value})}
                      placeholder="Ex: Première ville du programme..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="launch-date">Date de lancement (optionnel)</Label>
                    <Input
                      id="launch-date"
                      type="date"
                      value={cityForm.launch_date}
                      onChange={(e) => setCityForm({...cityForm, launch_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Switch
                      id="city-active"
                      checked={cityForm.is_active}
                      onCheckedChange={(checked) => setCityForm({...cityForm, is_active: checked})}
                    />
                    <Label htmlFor="city-active">Ville active (visible sur le site)</Label>
                  </div>
                </div>
                
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setIsCityEditorOpen(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleSaveCity} className="bg-blue-500 hover:bg-blue-600 text-white">
                    <Save className="w-4 h-4 mr-2" />
                    {editingCity ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        );

      case 'veterinaires-content':
        return <AdminVeterinairesContent />;

      case 'promotion':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <Gift className="w-6 h-6 text-orange-500" />
                <h2 className="text-xl font-semibold">Gestion de la Promotion</h2>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                Configurez l'offre promotionnelle pour les nouveaux vétérinaires. Le nombre de mois offerts sera affiché sur le site et appliqué lors de l'inscription.
              </p>

              {/* Preview Banner */}
              <div className="bg-gradient-to-r from-orange-500 to-yellow-400 rounded-xl p-6 text-white mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Gift className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Aperçu de la promotion</h3>
                    <p className="text-orange-100">
                      {(settings.promotion?.trialMonths || 2)} mois offerts - Premier prélèvement dans {(settings.promotion?.trialMonths || 2) * 30} jours
                    </p>
                  </div>
                  <div className="ml-auto">
                    <span className="bg-white text-orange-500 px-4 py-2 rounded-full font-bold">
                      {settings.promotion?.trialMonths || 2} MOIS GRATUITS
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Enable/Disable */}
                <div className="border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label className="text-base font-medium">Activer la promotion</Label>
                      <p className="text-sm text-gray-500">Affiche le bandeau et les offres sur le site</p>
                    </div>
                    <Switch 
                      checked={settings.promotion?.enabled !== false}
                      onCheckedChange={(checked) => {
                        updateSettings({
                          ...settings,
                          promotion: { ...settings.promotion, enabled: checked }
                        });
                        toast.success(checked ? 'Promotion activée' : 'Promotion désactivée');
                      }}
                    />
                  </div>
                </div>

                {/* Trial Months */}
                <div className="border rounded-xl p-4">
                  <Label className="text-base font-medium">Nombre de mois offerts</Label>
                  <p className="text-sm text-gray-500 mb-3">Période d'essai gratuite pour les vétérinaires</p>
                  <div className="flex items-center gap-4">
                    <Input 
                      type="number"
                      min="1"
                      max="12"
                      value={settings.promotion?.trialMonths || 2}
                      onChange={(e) => {
                        const months = Math.max(1, Math.min(12, parseInt(e.target.value) || 2));
                        updateSettings({
                          ...settings,
                          promotion: { ...settings.promotion, trialMonths: months }
                        });
                      }}
                      className="w-24"
                    />
                    <span className="text-gray-500">mois</span>
                    <div className="flex gap-2 ml-auto">
                      {[1, 2, 3, 6].map(m => (
                        <Button
                          key={m}
                          variant={settings.promotion?.trialMonths === m ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            updateSettings({
                              ...settings,
                              promotion: { ...settings.promotion, trialMonths: m }
                            });
                            toast.success(`Promotion mise à jour : ${m} mois offerts`);
                          }}
                        >
                          {m} mois
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bandeau Settings */}
              <div className="border rounded-xl p-4 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-base font-medium">Bandeau défilant sur la homepage</Label>
                    <p className="text-sm text-gray-500">Affiche un bandeau promotionnel en haut de la page d'accueil</p>
                  </div>
                  <Switch 
                    checked={settings.promotion?.bannerEnabled !== false}
                    onCheckedChange={(checked) => {
                      updateSettings({
                        ...settings,
                        promotion: { ...settings.promotion, bannerEnabled: checked }
                      });
                      toast.success(checked ? 'Bandeau activé' : 'Bandeau désactivé');
                    }}
                  />
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mt-6">
                <h4 className="font-medium text-blue-800 mb-2">ℹ️ Fonctionnement de la promotion</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• La carte bancaire est enregistrée à l'inscription (validation Stripe)</li>
                  <li>• Une facture à 0€ est générée chaque mois pendant la période d'essai</li>
                  <li>• Le premier prélèvement réel intervient après les {settings.promotion?.trialMonths || 2} mois offerts</li>
                  <li>• Le vétérinaire peut annuler à tout moment pendant l'essai</li>
                </ul>
              </div>

              <Button 
                onClick={() => toast.success('Paramètres de promotion enregistrés !')}
                className="mt-6 bg-orange-500 hover:bg-orange-600"
              >
                <Save className="w-4 h-4 mr-2" />
                Enregistrer les modifications
              </Button>
            </div>
          </div>
        );

      case 'cms':
        return <AdminContentManager adminToken={localStorage.getItem('adminToken')} />;

      case 'pricing':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-semibold">Gestion des Tarifs Vétérinaires</h2>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                Configurez les 3 offres d&apos;abonnement pour les vétérinaires. Les modifications seront visibles sur la page &quot;Espace Vétérinaire&quot;.
              </p>

              <div className="grid gap-6">
                {(pricingForm.plans || []).map((plan, index) => (
                  <div key={plan.id} className={`border-2 rounded-xl p-6 ${plan.popular ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full ${plan.color === 'green' ? 'bg-green-500' : plan.color === 'blue' ? 'bg-blue-500' : 'bg-purple-500'}`}></div>
                        <h3 className="text-lg font-bold">{plan.name}</h3>
                        {plan.popular && <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">POPULAIRE</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={plan.active !== false} 
                          onCheckedChange={checked => {
                            const updated = [...(pricingForm.plans || [])];
                            updated[index] = { ...updated[index], active: checked };
                            setPricingForm({...pricingForm, plans: updated});
                          }} 
                        />
                        <Label>Actif</Label>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <Label>Nom de l&apos;offre</Label>
                        <Input 
                          value={plan.name || ''} 
                          onChange={e => {
                            const updated = [...(pricingForm.plans || [])];
                            updated[index] = { ...updated[index], name: e.target.value };
                            setPricingForm({...pricingForm, plans: updated});
                          }} 
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Prix (€/mois)</Label>
                        <Input 
                          type="number"
                          value={plan.price || 0} 
                          onChange={e => {
                            const updated = [...(pricingForm.plans || [])];
                            updated[index] = { ...updated[index], price: parseInt(e.target.value) || 0 };
                            setPricingForm({...pricingForm, plans: updated});
                          }} 
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Max vétérinaires</Label>
                        <Input 
                          type="number"
                          value={plan.maxVets || 1} 
                          onChange={e => {
                            const updated = [...(pricingForm.plans || [])];
                            updated[index] = { ...updated[index], maxVets: parseInt(e.target.value) || 1 };
                            setPricingForm({...pricingForm, plans: updated});
                          }} 
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>RDV/mois</Label>
                        <Input 
                          type="number"
                          value={plan.appointmentsLimit || 100} 
                          onChange={e => {
                            const updated = [...(pricingForm.plans || [])];
                            updated[index] = { ...updated[index], appointmentsLimit: parseInt(e.target.value) || 100 };
                            setPricingForm({...pricingForm, plans: updated});
                          }} 
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label>Description</Label>
                        <Input 
                          value={plan.description || ''} 
                          onChange={e => {
                            const updated = [...(pricingForm.plans || [])];
                            updated[index] = { ...updated[index], description: e.target.value };
                            setPricingForm({...pricingForm, plans: updated});
                          }} 
                          className="mt-1"
                          placeholder="Idéal pour..."
                        />
                      </div>
                      <div>
                        <Label>Couleur</Label>
                        <select 
                          value={plan.color || 'green'} 
                          onChange={e => {
                            const updated = [...(pricingForm.plans || [])];
                            updated[index] = { ...updated[index], color: e.target.value };
                            setPricingForm({...pricingForm, plans: updated});
                          }} 
                          className="mt-1 w-full border rounded-md p-2"
                        >
                          <option value="green">🟢 Vert</option>
                          <option value="blue">🔵 Bleu</option>
                          <option value="purple">🟣 Violet</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-4">
                      <Switch 
                        checked={plan.popular || false} 
                        onCheckedChange={checked => {
                          const updated = (pricingForm.plans || []).map((p, i) => ({
                            ...p,
                            popular: i === index ? checked : false
                          }));
                          setPricingForm({...pricingForm, plans: updated});
                        }} 
                      />
                      <Label>Marquer comme &quot;Populaire&quot;</Label>
                    </div>

                    <div>
                      <Label>Fonctionnalités incluses (une par ligne)</Label>
                      <Textarea 
                        value={(plan.features || []).join('\n')} 
                        onChange={e => {
                          const updated = [...(pricingForm.plans || [])];
                          updated[index] = { ...updated[index], features: e.target.value.split('\n').filter(f => f.trim()) };
                          setPricingForm({...pricingForm, plans: updated});
                        }} 
                        className="mt-1"
                        rows={4}
                        placeholder="Agenda simple&#10;Rappels email&#10;100 RDV/mois"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                onClick={() => {
                  updateSettings({ pricing: pricingForm });
                  toast.success('Tarifs enregistrés');
                }} 
                className="mt-6 bg-green-500 hover:bg-green-600 text-white"
              >
                <Save className="w-4 h-4 mr-2" /> Enregistrer les tarifs
              </Button>
            </div>
          </div>
        );

      case 'homepage':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <Home className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-semibold">Gestion de la Page d&apos;accueil</h2>
              </div>

              {/* Hero Section */}
              <div className="border rounded-lg p-4 mb-6">
                <h3 className="font-medium mb-4">Section Hero</h3>
                <div className="grid gap-4">
                  <div>
                    <Label>Titre principal</Label>
                    <Input 
                      value={homepageForm.heroTitle || ''} 
                      onChange={e => setHomepageForm({...homepageForm, heroTitle: e.target.value})} 
                      className="mt-1" 
                      placeholder="Votre rendez-vous vétérinaire"
                    />
                  </div>
                  <div>
                    <Label>Titre mis en avant (couleur)</Label>
                    <Input 
                      value={homepageForm.heroTitleHighlight || ''} 
                      onChange={e => setHomepageForm({...homepageForm, heroTitleHighlight: e.target.value})} 
                      className="mt-1" 
                      placeholder="en quelques clics !"
                    />
                  </div>
                  <div>
                    <Label>Sous-titre</Label>
                    <Input 
                      value={homepageForm.heroSubtitle || ''} 
                      onChange={e => setHomepageForm({...homepageForm, heroSubtitle: e.target.value})} 
                      className="mt-1" 
                      placeholder="Simple, rapide et gratuit"
                    />
                  </div>
                  <div>
                    <Label>URL de l&apos;image Hero</Label>
                    <Input 
                      value={homepageForm.heroImage || ''} 
                      onChange={e => setHomepageForm({...homepageForm, heroImage: e.target.value})} 
                      className="mt-1" 
                      placeholder="https://..."
                    />
                    {homepageForm.heroImage && (
                      <img src={homepageForm.heroImage} alt="Hero preview" className="mt-2 w-full h-40 object-cover rounded-lg" />
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Texte du bouton</Label>
                      <Input 
                        value={homepageForm.heroButtonText || ''} 
                        onChange={e => setHomepageForm({...homepageForm, heroButtonText: e.target.value})} 
                        className="mt-1" 
                        placeholder="Rechercher"
                      />
                    </div>
                    <div>
                      <Label>Lien du bouton</Label>
                      <Input 
                        value={homepageForm.heroButtonLink || ''} 
                        onChange={e => setHomepageForm({...homepageForm, heroButtonLink: e.target.value})} 
                        className="mt-1" 
                        placeholder="/recherche"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Features Section */}
              <div className="border rounded-lg p-4 mb-6">
                <h3 className="font-medium mb-4">Fonctionnalités mises en avant</h3>
                <div className="space-y-3">
                  {(homepageForm.features || []).map((feature, index) => (
                    <div key={feature.id || index} className="flex items-start gap-3 p-3 border rounded-lg">
                      <div className="flex-1 grid grid-cols-3 gap-3">
                        <Input 
                          value={feature.title || ''} 
                          onChange={e => {
                            const updated = [...(homepageForm.features || [])];
                            updated[index] = { ...updated[index], title: e.target.value };
                            setHomepageForm({...homepageForm, features: updated});
                          }} 
                          placeholder="Titre"
                        />
                        <Input 
                          value={feature.description || ''} 
                          onChange={e => {
                            const updated = [...(homepageForm.features || [])];
                            updated[index] = { ...updated[index], description: e.target.value };
                            setHomepageForm({...homepageForm, features: updated});
                          }} 
                          placeholder="Description"
                          className="col-span-2"
                        />
                      </div>
                      <Switch 
                        checked={feature.active !== false} 
                        onCheckedChange={checked => {
                          const updated = [...(homepageForm.features || [])];
                          updated[index] = { ...updated[index], active: checked };
                          setHomepageForm({...homepageForm, features: updated});
                        }} 
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats Section */}
              <div className="border rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium">Statistiques affichées</h3>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={homepageForm.showStats !== false} 
                      onCheckedChange={checked => setHomepageForm({...homepageForm, showStats: checked})} 
                    />
                    <Label>Afficher les stats</Label>
                  </div>
                </div>
                <div className="space-y-3">
                  {(homepageForm.stats || []).map((stat, index) => (
                    <div key={stat.id || index} className="flex items-center gap-3 p-3 border rounded-lg">
                      <Input 
                        value={stat.value || ''} 
                        onChange={e => {
                          const updated = [...(homepageForm.stats || [])];
                          updated[index] = { ...updated[index], value: e.target.value };
                          setHomepageForm({...homepageForm, stats: updated});
                        }} 
                        placeholder="7000+"
                        className="w-32"
                      />
                      <Input 
                        value={stat.label || ''} 
                        onChange={e => {
                          const updated = [...(homepageForm.stats || [])];
                          updated[index] = { ...updated[index], label: e.target.value };
                          setHomepageForm({...homepageForm, stats: updated});
                        }} 
                        placeholder="Cliniques"
                        className="flex-1"
                      />
                      <Switch 
                        checked={stat.active !== false} 
                        onCheckedChange={checked => {
                          const updated = [...(homepageForm.stats || [])];
                          updated[index] = { ...updated[index], active: checked };
                          setHomepageForm({...homepageForm, stats: updated});
                        }} 
                      />
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={handleSaveHomepage} className="bg-green-500 hover:bg-green-600 text-white">
                <Save className="w-4 h-4 mr-2" /> Enregistrer la page d&apos;accueil
              </Button>
            </div>
          </div>
        );

      case 'theme':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <Palette className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-semibold">Thème & Couleurs du site</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Primary Colors */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium mb-4">Couleurs principales</h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Couleur primaire</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input 
                          type="color" 
                          value={themeForm.primaryColor || '#22c55e'} 
                          onChange={e => setThemeForm({...themeForm, primaryColor: e.target.value})} 
                          className="w-12 h-12 rounded cursor-pointer border"
                        />
                        <Input 
                          value={themeForm.primaryColor || '#22c55e'} 
                          onChange={e => setThemeForm({...themeForm, primaryColor: e.target.value})} 
                          className="flex-1" 
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Couleur primaire foncée</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input 
                          type="color" 
                          value={themeForm.primaryColorDark || '#16a34a'} 
                          onChange={e => setThemeForm({...themeForm, primaryColorDark: e.target.value})} 
                          className="w-12 h-12 rounded cursor-pointer border"
                        />
                        <Input 
                          value={themeForm.primaryColorDark || '#16a34a'} 
                          onChange={e => setThemeForm({...themeForm, primaryColorDark: e.target.value})} 
                          className="flex-1" 
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Couleur primaire claire</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input 
                          type="color" 
                          value={themeForm.primaryColorLight || '#86efac'} 
                          onChange={e => setThemeForm({...themeForm, primaryColorLight: e.target.value})} 
                          className="w-12 h-12 rounded cursor-pointer border"
                        />
                        <Input 
                          value={themeForm.primaryColorLight || '#86efac'} 
                          onChange={e => setThemeForm({...themeForm, primaryColorLight: e.target.value})} 
                          className="flex-1" 
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Secondary Colors */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium mb-4">Couleurs secondaires</h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Couleur secondaire</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input 
                          type="color" 
                          value={themeForm.secondaryColor || '#3b82f6'} 
                          onChange={e => setThemeForm({...themeForm, secondaryColor: e.target.value})} 
                          className="w-12 h-12 rounded cursor-pointer border"
                        />
                        <Input 
                          value={themeForm.secondaryColor || '#3b82f6'} 
                          onChange={e => setThemeForm({...themeForm, secondaryColor: e.target.value})} 
                          className="flex-1" 
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Couleur d&apos;accent</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input 
                          type="color" 
                          value={themeForm.accentColor || '#f59e0b'} 
                          onChange={e => setThemeForm({...themeForm, accentColor: e.target.value})} 
                          className="w-12 h-12 rounded cursor-pointer border"
                        />
                        <Input 
                          value={themeForm.accentColor || '#f59e0b'} 
                          onChange={e => setThemeForm({...themeForm, accentColor: e.target.value})} 
                          className="flex-1" 
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-6 border rounded-lg p-4">
                <h3 className="font-medium mb-4">Aperçu des couleurs</h3>
                <div className="flex gap-4 flex-wrap">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-lg" style={{ backgroundColor: themeForm.primaryColor || '#22c55e' }}></div>
                    <p className="text-xs mt-1">Primaire</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-lg" style={{ backgroundColor: themeForm.primaryColorDark || '#16a34a' }}></div>
                    <p className="text-xs mt-1">Primaire foncée</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-lg" style={{ backgroundColor: themeForm.primaryColorLight || '#86efac' }}></div>
                    <p className="text-xs mt-1">Primaire claire</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-lg" style={{ backgroundColor: themeForm.secondaryColor || '#3b82f6' }}></div>
                    <p className="text-xs mt-1">Secondaire</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-lg" style={{ backgroundColor: themeForm.accentColor || '#f59e0b' }}></div>
                    <p className="text-xs mt-1">Accent</p>
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveTheme} className="mt-6 bg-green-500 hover:bg-green-600 text-white">
                <Save className="w-4 h-4 mr-2" /> Enregistrer le thème
              </Button>
            </div>
          </div>
        );

      case 'social':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <Share2 className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-semibold">Réseaux sociaux</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 border rounded-lg">
                  <Facebook className="w-6 h-6 text-blue-600" />
                  <div className="flex-1">
                    <Label>Facebook</Label>
                    <Input 
                      value={socialForm.facebook || ''} 
                      onChange={e => setSocialForm({...socialForm, facebook: e.target.value})} 
                      className="mt-1" 
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 border rounded-lg">
                  <Instagram className="w-6 h-6 text-pink-600" />
                  <div className="flex-1">
                    <Label>Instagram</Label>
                    <Input 
                      value={socialForm.instagram || ''} 
                      onChange={e => setSocialForm({...socialForm, instagram: e.target.value})} 
                      className="mt-1" 
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 border rounded-lg">
                  <Twitter className="w-6 h-6 text-sky-500" />
                  <div className="flex-1">
                    <Label>Twitter / X</Label>
                    <Input 
                      value={socialForm.twitter || ''} 
                      onChange={e => setSocialForm({...socialForm, twitter: e.target.value})} 
                      className="mt-1" 
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 border rounded-lg">
                  <Linkedin className="w-6 h-6 text-blue-700" />
                  <div className="flex-1">
                    <Label>LinkedIn</Label>
                    <Input 
                      value={socialForm.linkedin || ''} 
                      onChange={e => setSocialForm({...socialForm, linkedin: e.target.value})} 
                      className="mt-1" 
                      placeholder="https://linkedin.com/..."
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 border rounded-lg">
                  <Youtube className="w-6 h-6 text-red-600" />
                  <div className="flex-1">
                    <Label>YouTube</Label>
                    <Input 
                      value={socialForm.youtube || ''} 
                      onChange={e => setSocialForm({...socialForm, youtube: e.target.value})} 
                      className="mt-1" 
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 border rounded-lg">
                  <Globe className="w-6 h-6 text-gray-600" />
                  <div className="flex-1">
                    <Label>TikTok</Label>
                    <Input 
                      value={socialForm.tiktok || ''} 
                      onChange={e => setSocialForm({...socialForm, tiktok: e.target.value})} 
                      className="mt-1" 
                      placeholder="https://tiktok.com/..."
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveSocial} className="mt-6 bg-green-500 hover:bg-green-600 text-white">
                <Save className="w-4 h-4 mr-2" /> Enregistrer les réseaux sociaux
              </Button>
            </div>
          </div>
        );

      case 'contact-page':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <Contact className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-semibold">Page Contact</h2>
              </div>

              {/* Contact Info */}
              <div className="border rounded-lg p-4 mb-6">
                <h3 className="font-medium mb-4">Informations de contact</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <Input 
                      value={contactForm.email || ''} 
                      onChange={e => setContactForm({...contactForm, email: e.target.value})} 
                      className="mt-1" 
                      placeholder="contact@vetordv.fr"
                    />
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <Input 
                      value={contactForm.phone || ''} 
                      onChange={e => setContactForm({...contactForm, phone: e.target.value})} 
                      className="mt-1" 
                      placeholder="01 23 45 67 89"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Adresse</Label>
                    <Input 
                      value={contactForm.address || ''} 
                      onChange={e => setContactForm({...contactForm, address: e.target.value})} 
                      className="mt-1" 
                      placeholder="123 Avenue des Animaux"
                    />
                  </div>
                  <div>
                    <Label>Code postal</Label>
                    <Input 
                      value={contactForm.postalCode || ''} 
                      onChange={e => setContactForm({...contactForm, postalCode: e.target.value})} 
                      className="mt-1" 
                      placeholder="75001"
                    />
                  </div>
                  <div>
                    <Label>Ville</Label>
                    <Input 
                      value={contactForm.city || ''} 
                      onChange={e => setContactForm({...contactForm, city: e.target.value})} 
                      className="mt-1" 
                      placeholder="Paris"
                    />
                  </div>
                </div>
              </div>

              {/* Opening Hours */}
              <div className="border rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium">Horaires d&apos;ouverture</h3>
                  <Button size="sm" onClick={handleAddOpeningHour} className="bg-green-500 hover:bg-green-600 text-white">
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {(contactForm.openingHours || []).map((hour, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <Input 
                        value={hour.day || ''} 
                        onChange={e => handleUpdateOpeningHour(index, 'day', e.target.value)} 
                        placeholder="Lundi - Vendredi"
                        className="flex-1"
                      />
                      <Input 
                        value={hour.hours || ''} 
                        onChange={e => handleUpdateOpeningHour(index, 'hours', e.target.value)} 
                        placeholder="9h00 - 18h00"
                        className="flex-1"
                      />
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteOpeningHour(index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Map */}
              <div className="border rounded-lg p-4 mb-6">
                <h3 className="font-medium mb-4">Carte Google Maps</h3>
                <div>
                  <Label>URL d&apos;intégration Google Maps</Label>
                  <Textarea 
                    value={contactForm.mapUrl || ''} 
                    onChange={e => setContactForm({...contactForm, mapUrl: e.target.value})} 
                    className="mt-1" 
                    rows={3}
                    placeholder="https://www.google.com/maps/embed?..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Collez ici le lien d&apos;intégration de Google Maps (iframe src)
                  </p>
                </div>
                {contactForm.mapUrl && (
                  <div className="mt-4">
                    <iframe 
                      src={contactForm.mapUrl} 
                      width="100%" 
                      height="200" 
                      style={{ border: 0, borderRadius: '8px' }} 
                      allowFullScreen 
                      loading="lazy"
                      title="Map"
                    ></iframe>
                  </div>
                )}
              </div>

              <Button onClick={handleSaveContact} className="bg-green-500 hover:bg-green-600 text-white">
                <Save className="w-4 h-4 mr-2" /> Enregistrer la page contact
              </Button>
            </div>
          </div>
        );

      case 'legal':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <Scale className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-semibold">Pages légales</h2>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 mb-6 border-b">
                <button 
                  onClick={() => setActiveLegalTab('mentionsLegales')}
                  className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                    activeLegalTab === 'mentionsLegales' 
                      ? 'border-green-500 text-green-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Mentions légales
                </button>
                <button 
                  onClick={() => setActiveLegalTab('cgu')}
                  className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                    activeLegalTab === 'cgu' 
                      ? 'border-green-500 text-green-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  CGU
                </button>
                <button 
                  onClick={() => setActiveLegalTab('confidentialite')}
                  className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                    activeLegalTab === 'confidentialite' 
                      ? 'border-green-500 text-green-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Confidentialité
                </button>
              </div>

              {/* Content */}
              {legalForm[activeLegalTab] && (
                <div className="space-y-6">
                  {/* Meta info */}
                  <div className="grid md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <Label>Titre de la page</Label>
                      <Input 
                        value={legalForm[activeLegalTab]?.title || ''} 
                        onChange={e => handleUpdateLegalMeta(activeLegalTab, 'title', e.target.value)} 
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Dernière mise à jour</Label>
                      <Input 
                        value={legalForm[activeLegalTab]?.lastUpdate || ''} 
                        onChange={e => handleUpdateLegalMeta(activeLegalTab, 'lastUpdate', e.target.value)} 
                        className="mt-1"
                        placeholder="Janvier 2025"
                      />
                    </div>
                  </div>

                  {/* Sections */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="font-medium">Sections</h3>
                      <Button 
                        size="sm" 
                        onClick={() => handleAddLegalSection(activeLegalTab)}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Ajouter une section
                      </Button>
                    </div>
                    
                    {(legalForm[activeLegalTab]?.sections || []).map((section, index) => (
                      <div key={section.id || index} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <Input 
                            value={section.title || ''} 
                            onChange={e => handleUpdateLegalSection(activeLegalTab, index, 'title', e.target.value)} 
                            className="font-semibold text-lg flex-1 mr-2"
                            placeholder="Titre de la section"
                          />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteLegalSection(activeLegalTab, index)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        <Textarea 
                          value={section.content || ''} 
                          onChange={e => handleUpdateLegalSection(activeLegalTab, index, 'content', e.target.value)} 
                          className="min-h-[150px]"
                          placeholder="Contenu de la section (supporte le markdown basique : **gras**, • listes)"
                        />
                      </div>
                    ))}
                  </div>

                  <Button onClick={handleSaveLegal} className="bg-green-500 hover:bg-green-600 text-white">
                    <Save className="w-4 h-4 mr-2" /> Enregistrer les pages légales
                  </Button>
                </div>
              )}
            </div>
          </div>
        );

      case 'contenu':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-6 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">Contenu de la page d'accueil</h2>
                  <p className="text-sm text-gray-500">Modifiez les textes affichés sur votre site</p>
                </div>
                <Button onClick={saveHomepageContent} className="bg-green-500 hover:bg-green-600 text-white">
                  <Save className="w-4 h-4 mr-2" /> Enregistrer
                </Button>
              </div>
              
              <div className="p-6">
                {loadingContent ? (
                  <div className="text-center py-8 text-gray-400">Chargement...</div>
                ) : homepageContent ? (
                  <div className="space-y-6">
                    {/* Tagline */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Slogan du site</Label>
                      <Input
                        value={homepageContent.tagline || ''}
                        onChange={(e) => setHomepageContent({...homepageContent, tagline: e.target.value})}
                        className="mt-1"
                        placeholder="Trouvez un vétérinaire et prenez rendez-vous..."
                      />
                    </div>

                    {/* Features Section */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-3 block">
                        Les 3 points forts (affichés sur la page d'accueil)
                      </Label>
                      <div className="space-y-4">
                        {homepageContent.features?.map((feature, index) => (
                          <div key={feature.id} className="p-4 bg-gray-50 rounded-lg border">
                            <div className="flex items-center gap-3 mb-3">
                              <span className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold">
                                {index + 1}
                              </span>
                              <Input
                                value={feature.title}
                                onChange={(e) => updateFeature(feature.id, 'title', e.target.value)}
                                className="font-medium"
                                placeholder="Titre"
                              />
                            </div>
                            <Textarea
                              value={feature.description}
                              onChange={(e) => updateFeature(feature.id, 'description', e.target.value)}
                              rows={2}
                              className="w-full"
                              placeholder="Description..."
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Stats */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-3 block">Statistiques</Label>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-gray-500">Nombre de cliniques</Label>
                          <Input
                            value={homepageContent.stats?.clinics_count || '7000'}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent, 
                              stats: {...(homepageContent.stats || {}), clinics_count: e.target.value}
                            })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Nombre d'utilisateurs</Label>
                          <Input
                            value={homepageContent.stats?.users_count || '50000'}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent, 
                              stats: {...(homepageContent.stats || {}), users_count: e.target.value}
                            })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Nombre de RDV</Label>
                          <Input
                            value={homepageContent.stats?.appointments_count || '100000'}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent, 
                              stats: {...(homepageContent.stats || {}), appointments_count: e.target.value}
                            })}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    Aucun contenu trouvé. Cliquez pour initialiser.
                    <Button onClick={fetchHomepageContent} className="ml-2">Recharger</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'veterinaires':
        const pendingVets = veterinarians.filter(v => v.status === 'pending');
        const activeVets = veterinarians.filter(v => v.status === 'approved' || v.status === 'active');
        const rejectedVets = veterinarians.filter(v => v.status === 'rejected');
        
        return (
          <div className="space-y-6">
            {/* Pending Approvals */}
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-6 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">Inscriptions en attente</h2>
                  <p className="text-sm text-gray-500">Validez ou refusez les nouvelles demandes</p>
                </div>
                <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-medium">
                  {pendingVets.length} en attente
                </span>
              </div>
              <div className="p-6">
                {loadingVets ? (
                  <div className="text-center py-8 text-gray-400">Chargement...</div>
                ) : pendingVets.length > 0 ? (
                  <div className="space-y-4">
                    {pendingVets.map((vet) => (
                      <div key={vet.user_id} className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-800">{vet.clinic_name || vet.name}</p>
                          <p className="text-sm text-gray-500">{vet.email}</p>
                          <p className="text-sm text-gray-500">{vet.city || 'Ville non renseignée'} • {vet.phone || 'Téléphone non renseigné'}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => viewProfileDetails(vet.user_id, 'vet')} className="text-blue-600 border-blue-300 hover:bg-blue-50">
                            <Eye className="w-4 h-4 mr-1" /> Voir
                          </Button>
                          <Button size="sm" onClick={() => approveVeterinarian(vet.user_id)} className="bg-green-500 hover:bg-green-600 text-white">
                            <CheckCircle className="w-4 h-4 mr-1" /> Approuver
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => rejectVeterinarian(vet.user_id)} className="text-amber-600 border-amber-300 hover:bg-amber-50">
                            <XOctagon className="w-4 h-4 mr-1" /> Refuser
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deleteVeterinarian(vet.user_id, vet.clinic_name || vet.name)} className="text-red-600 border-red-300 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Aucune inscription en attente</p>
                    <p className="text-sm">Les nouvelles demandes apparaîtront ici</p>
                  </div>
                )}
              </div>
            </div>

            {/* Active Veterinarians */}
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-6 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">Vétérinaires actifs</h2>
                  <p className="text-sm text-gray-500">Liste des cabinets approuvés</p>
                </div>
                <div className="flex gap-2">
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                    {activeVets.length} actifs
                  </span>
                  <Button size="sm" onClick={fetchVeterinarians} variant="outline">
                    <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
                  </Button>
                </div>
              </div>
              <div className="p-6">
                {activeVets.length > 0 ? (
                  <div className="space-y-3">
                    {activeVets.map((vet) => (
                      <div key={vet.user_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-800">{vet.clinic_name || vet.name}</p>
                          <p className="text-sm text-gray-500">{vet.email}</p>
                          <p className="text-xs text-gray-400">{vet.city} • {vet.phone}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            vet.subscription_status === 'active' ? 'bg-green-100 text-green-700' :
                            vet.subscription_status === 'trial' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {vet.subscription_status === 'active' ? 'Abonné' :
                             vet.subscription_status === 'trial' ? 'Essai' : 'Inactif'}
                          </span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => viewProfileDetails(vet.user_id, 'vet')}>
                                <Eye className="w-4 h-4 mr-2" /> Voir la fiche
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => deleteVeterinarian(vet.user_id, vet.clinic_name || vet.name)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <Stethoscope className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Aucun vétérinaire actif</p>
                  </div>
                )}
              </div>
            </div>

            {/* Rejected Veterinarians */}
            {rejectedVets.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-semibold text-red-600">Inscriptions refusées</h2>
                  <p className="text-sm text-gray-500">{rejectedVets.length} demandes refusées</p>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {rejectedVets.map((vet) => (
                      <div key={vet.user_id} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-800">{vet.clinic_name || vet.name}</p>
                          <p className="text-sm text-gray-500">{vet.email}</p>
                        </div>
                        <Button size="sm" onClick={() => approveVeterinarian(vet.user_id)} className="bg-green-500 hover:bg-green-600 text-white">
                          <CheckCircle className="w-4 h-4 mr-1" /> Réactiver
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'patients':
        const pendingPatients = patients.filter(p => p.status === 'pending');
        const activePatients = patients.filter(p => p.status === 'approved' || p.status === 'active');
        const rejectedPatients = patients.filter(p => p.status === 'rejected');
        
        return (
          <div className="space-y-6">
            {/* Pending Patient Approvals */}
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-6 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">Inscriptions patients en attente</h2>
                  <p className="text-sm text-gray-500">Validez ou refusez les nouvelles demandes</p>
                </div>
                <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-medium">
                  {pendingPatients.length} en attente
                </span>
              </div>
              <div className="p-6">
                {loadingPatients ? (
                  <div className="text-center py-8 text-gray-400">Chargement...</div>
                ) : pendingPatients.length > 0 ? (
                  <div className="space-y-4">
                    {pendingPatients.map((patient) => (
                      <div key={patient.user_id} className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-800">{patient.name}</p>
                          <p className="text-sm text-gray-500">{patient.email}</p>
                          <p className="text-xs text-gray-400">{patient.city || 'Ville non renseignée'}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => viewProfileDetails(patient.user_id, 'patient')} className="text-blue-600 border-blue-300 hover:bg-blue-50">
                            <Eye className="w-4 h-4 mr-1" /> Voir
                          </Button>
                          <Button size="sm" onClick={() => approvePatient(patient.user_id)} className="bg-green-500 hover:bg-green-600 text-white">
                            <CheckCircle className="w-4 h-4 mr-1" /> Approuver
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => rejectPatient(patient.user_id)} className="text-amber-600 border-amber-300 hover:bg-amber-50">
                            <XOctagon className="w-4 h-4 mr-1" /> Refuser
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deletePatient(patient.user_id, patient.name)} className="text-red-600 border-red-300 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Aucune inscription en attente</p>
                    <p className="text-sm">Les nouvelles demandes apparaîtront ici</p>
                  </div>
                )}
              </div>
            </div>

            {/* Active Patients */}
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-6 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">Patients actifs</h2>
                  <p className="text-sm text-gray-500">Liste des patients approuvés</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                    {activePatients.length} patients
                  </span>
                  <Button size="sm" onClick={fetchPatients} variant="outline">
                    <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
                  </Button>
                </div>
              </div>
              <div className="p-6">
                {activePatients.length > 0 ? (
                  <div className="space-y-3">
                    {activePatients.map((patient) => (
                      <div key={patient.user_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-800">{patient.name}</p>
                          <p className="text-sm text-gray-500">{patient.email}</p>
                          <p className="text-xs text-gray-400">{patient.city || 'Ville non renseignée'} • {patient.phone || 'Téléphone non renseigné'}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm text-gray-600">{patient.animals_count || 0} animaux</p>
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                              Approuvé
                            </span>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => viewProfileDetails(patient.user_id, 'patient')}>
                                <Eye className="w-4 h-4 mr-2" /> Voir la fiche
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => deletePatient(patient.user_id, patient.name)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Aucun patient actif</p>
                    <p className="text-sm">Les patients approuvés apparaîtront ici</p>
                  </div>
                )}
              </div>
            </div>

            {/* Rejected Patients */}
            {rejectedPatients.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-semibold text-red-600">Inscriptions refusées</h2>
                  <p className="text-sm text-gray-500">{rejectedPatients.length} demandes refusées</p>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {rejectedPatients.map((patient) => (
                      <div key={patient.user_id} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-800">{patient.name}</p>
                          <p className="text-sm text-gray-500">{patient.email}</p>
                        </div>
                        <Button size="sm" onClick={() => approvePatient(patient.user_id)} className="bg-green-500 hover:bg-green-600 text-white">
                          <CheckCircle className="w-4 h-4 mr-1" /> Réactiver
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-6 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">Paiements</h2>
                  <p className="text-sm text-gray-500">Historique des transactions</p>
                </div>
                <div className="flex gap-2">
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                    0€ ce mois
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="text-center py-8 text-gray-400">
                  <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucun paiement</p>
                  <p className="text-sm">Les paiements des abonnements apparaîtront ici</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'articles':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6 flex justify-between items-center">
              <div><h2 className="text-xl font-semibold">{articles.length} Articles</h2></div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={resetArticles}><RefreshCw className="w-4 h-4 mr-2" />Réinitialiser</Button>
                <Button onClick={() => { setEditingArticle(null); setIsEditorOpen(true); }} className="bg-green-500 hover:bg-green-600 text-white"><Plus className="w-4 h-4 mr-2" />Nouvel article</Button>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {articles.map((a) => (
                <div key={a.id} className="bg-white rounded-xl shadow-sm overflow-hidden group">
                  <div className="relative h-40"><img src={a.image} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button size="sm" variant="secondary" onClick={() => { setEditingArticle(a); setIsEditorOpen(true); }}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteClick(a, 'article')}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <div className="p-4"><h3 className="font-semibold line-clamp-2">{a.title}</h3><p className="text-sm text-gray-500 mt-1">{a.date}</p></div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'faq':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6 flex justify-between items-center">
              <h2 className="text-xl font-semibold">{faqs.length} Questions FAQ</h2>
              <Button onClick={() => { setEditingFAQ(null); setFaqForm({ question: '', answer: '' }); setIsFAQEditorOpen(true); }} className="bg-green-500 hover:bg-green-600 text-white"><Plus className="w-4 h-4 mr-2" />Nouvelle FAQ</Button>
            </div>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.id} className={`bg-white rounded-xl shadow-sm p-6 ${!faq.active ? 'opacity-50' : ''}`}>
                  <div className="flex justify-between items-start gap-4">
                    <div><h3 className="font-semibold">{faq.question}</h3><p className="text-gray-600 text-sm mt-2">{faq.answer}</p></div>
                    <div className="flex items-center gap-2">
                      <Switch checked={faq.active} onCheckedChange={() => toggleFAQActive(faq.id)} />
                      <Button variant="ghost" size="sm" onClick={() => { setEditingFAQ(faq); setFaqForm({ question: faq.question, answer: faq.answer }); setIsFAQEditorOpen(true); }}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(faq, 'faq')}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'reviews':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm"><p className="text-2xl font-bold">{reviewStats.total}</p><p className="text-sm text-gray-500">Total</p></div>
              <div className="bg-yellow-50 rounded-xl p-4"><p className="text-2xl font-bold text-yellow-700">{reviewStats.pending}</p><p className="text-sm text-yellow-600">En attente</p></div>
              <div className="bg-green-50 rounded-xl p-4"><p className="text-2xl font-bold text-green-700">{reviewStats.approved}</p><p className="text-sm text-green-600">Approuvés</p></div>
              <div className="bg-red-50 rounded-xl p-4"><p className="text-2xl font-bold text-red-700">{reviewStats.rejected}</p><p className="text-sm text-red-600">Rejetés</p></div>
            </div>
            <div className="space-y-4">
              {reviews.map((r) => (
                <div key={r.id} className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold">{r.author}</span>
                        <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= r.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />)}</div>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${r.status === 'approved' ? 'bg-green-100 text-green-700' : r.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{r.status}</span>
                      </div>
                      <p className="text-sm text-gray-500">{r.cliniqueName}</p>
                      <p className="text-gray-600 mt-2">{r.comment}</p>
                    </div>
                    <div className="flex gap-2">
                      {r.status === 'pending' && (<><Button size="sm" onClick={() => { approveReview(r.id); toast.success('Approuvé'); }} className="bg-green-500 text-white"><Check className="w-4 h-4" /></Button><Button size="sm" variant="destructive" onClick={() => { rejectReview(r.id); toast.success('Rejeté'); }}><XCircle className="w-4 h-4" /></Button></>)}
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteClick(r, 'review')}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'ads':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Gestion des Publicités</h2>
                <p className="text-sm text-gray-500">{adsStats.active} actives • {adsStats.totalRevenue}€ revenus • {adsStats.totalClicks} clics</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={resetAds}><RefreshCw className="w-4 h-4 mr-2" />Réinitialiser</Button>
                <Button onClick={handleCreateAd} className="bg-green-500 hover:bg-green-600 text-white"><Plus className="w-4 h-4 mr-2" />Nouvelle publicité</Button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {ads.map((ad) => (
                <div key={ad.id} className={`bg-white rounded-xl shadow-sm overflow-hidden ${!ad.active ? 'opacity-50' : ''}`}>
                  <div className="relative h-32"><img src={ad.imageUrl} alt={ad.altText} className="w-full h-full object-cover" />
                    <div className="absolute top-2 right-2 flex gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${ad.active ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>{ad.active ? 'Active' : 'Inactive'}</span>
                      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500 text-white">{ad.position}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold">{ad.name}</h3>
                    <p className="text-sm text-gray-500">{ad.advertiser} • {ad.price}€</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>{ad.impressions} impressions</span>
                      <span>{ad.clicks} clics</span>
                      <span>{ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(1) : 0}% CTR</span>
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <Switch checked={ad.active} onCheckedChange={() => { toggleAdActive(ad.id); toast.success(ad.active ? 'Désactivée' : 'Activée'); }} />
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEditAd(ad)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(ad, 'ad')}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'products':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Boutique - Produits Affiliés</h2>
                <p className="text-sm text-gray-500">{productsStats.active} actifs • {productsStats.featured} vedettes</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={resetProducts}><RefreshCw className="w-4 h-4 mr-2" />Réinitialiser</Button>
                <Button onClick={handleCreateProduct} className="bg-green-500 hover:bg-green-600 text-white"><Plus className="w-4 h-4 mr-2" />Nouveau produit</Button>
              </div>
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((product) => (
                <div key={product.id} className={`bg-white rounded-xl shadow-sm overflow-hidden ${!product.active ? 'opacity-50' : ''}`}>
                  <div className="relative h-40"><img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                    {product.featured && <span className="absolute top-2 left-2 px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded">Vedette</span>}
                    <span className="absolute top-2 right-2 px-2 py-1 bg-black/50 text-white text-xs rounded">{product.category}</span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-sm line-clamp-2">{product.name}</h3>
                    <p className="text-xs text-gray-500">{product.brand}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="font-bold text-green-600">{product.price}€</span>
                      {product.originalPrice > product.price && <span className="text-xs text-gray-400 line-through">{product.originalPrice}€</span>}
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { toggleProductFeatured(product.id); toast.success('Mis à jour'); }} title="Vedette">
                          <Star className={`w-4 h-4 ${product.featured ? 'text-yellow-400 fill-yellow-400' : 'text-gray-400'}`} />
                        </Button>
                        <Switch checked={product.active} onCheckedChange={() => { toggleProductActive(product.id); toast.success(product.active ? 'Désactivé' : 'Activé'); }} />
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditProduct(product)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(product, 'product')}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'header-footer':
        return (
          <div className="space-y-6">
            {/* Header Section */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <PanelTop className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-semibold">Gestion du Header</h2>
              </div>
              
              <div className="space-y-6">
                {/* Logo Settings */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium mb-4">Logo</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Texte partie 1 (foncé)</Label>
                      <Input 
                        value={headerForm.logoTextPart1 || 'VETO'} 
                        onChange={e => setHeaderForm({...headerForm, logoTextPart1: e.target.value})} 
                        className="mt-1" 
                        placeholder="VETO"
                      />
                    </div>
                    <div>
                      <Label>Texte partie 2 (couleur)</Label>
                      <Input 
                        value={headerForm.logoTextPart2 || 'RDV'} 
                        onChange={e => setHeaderForm({...headerForm, logoTextPart2: e.target.value})} 
                        className="mt-1" 
                        placeholder="RDV"
                      />
                    </div>
                  </div>

                  {/* Logo Type */}
                  <div className="mt-4">
                    <Label>Type de logo</Label>
                    <select 
                      value={headerForm.logoType || 'default'} 
                      onChange={e => setHeaderForm({...headerForm, logoType: e.target.value})} 
                      className="mt-1 w-full border rounded-md p-2"
                    >
                      <option value="default">Icône par défaut (patte)</option>
                      <option value="custom">Image personnalisée</option>
                      <option value="none">Aucun (texte uniquement)</option>
                    </select>
                  </div>

                  {/* Custom Logo URL */}
                  {headerForm.logoType === 'custom' && (
                    <div className="mt-4 space-y-4">
                      {/* Upload Logo */}
                      <div>
                        <Label>Téléverser un logo</Label>
                        <div className="mt-2 flex items-center gap-4">
                          <label className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg cursor-pointer transition-colors">
                            <Image className="w-4 h-4" />
                            <span>Choisir une image</span>
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                // Validate size
                                if (file.size > 2 * 1024 * 1024) {
                                  toast.error('Le fichier est trop volumineux (max 2MB)');
                                  return;
                                }
                                
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                try {
                                  const token = localStorage.getItem('adminToken');
                                  const response = await fetch(`${API_URL}/api/admin/upload-logo`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` },
                                    body: formData
                                  });
                                  
                                  if (response.ok) {
                                    const data = await response.json();
                                    setHeaderForm({...headerForm, customLogoUrl: `${API_URL}${data.logo_url}`});
                                    toast.success('Logo téléversé avec succès');
                                  } else {
                                    const err = await response.json();
                                    toast.error(err.detail || 'Erreur lors du téléversement');
                                  }
                                } catch (error) {
                                  toast.error('Erreur de connexion');
                                }
                              }}
                              className="hidden"
                            />
                          </label>
                          <span className="text-sm text-gray-500">PNG, JPG, SVG ou WebP (max 2MB)</span>
                        </div>
                      </div>
                      
                      {/* Or URL */}
                      <div className="flex items-center gap-4">
                        <div className="flex-1 border-t border-gray-200"></div>
                        <span className="text-sm text-gray-400">ou</span>
                        <div className="flex-1 border-t border-gray-200"></div>
                      </div>
                      
                      {/* Manual URL */}
                      <div>
                        <Label>URL de l&apos;image</Label>
                        <Input 
                          value={headerForm.customLogoUrl || ''} 
                          onChange={e => setHeaderForm({...headerForm, customLogoUrl: e.target.value})} 
                          className="mt-1" 
                          placeholder="https://exemple.com/logo.png"
                        />
                      </div>
                      
                      {/* Preview */}
                      {headerForm.customLogoUrl && (
                        <div className="flex items-center gap-4 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                          <img 
                            src={headerForm.customLogoUrl} 
                            alt="Aperçu logo" 
                            className="w-12 h-12 object-contain"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium">Aperçu du logo</p>
                            <p className="text-xs text-gray-500 truncate max-w-xs">{headerForm.customLogoUrl}</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setHeaderForm({...headerForm, customLogoUrl: ''})}
                            className="text-red-500 border-red-300 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Logo Colors */}
                  {headerForm.logoType !== 'custom' && (
                    <div className="mt-4 grid grid-cols-2 gap-4">
                      <div>
                        <Label>Couleur principale</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input 
                            type="color" 
                            value={headerForm.logoColor || '#22c55e'} 
                            onChange={e => setHeaderForm({...headerForm, logoColor: e.target.value})} 
                            className="w-10 h-10 rounded cursor-pointer border"
                          />
                          <Input 
                            value={headerForm.logoColor || '#22c55e'} 
                            onChange={e => setHeaderForm({...headerForm, logoColor: e.target.value})} 
                            className="flex-1" 
                            placeholder="#22c55e"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Couleur secondaire (foncée)</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input 
                            type="color" 
                            value={headerForm.logoColorDark || '#16a34a'} 
                            onChange={e => setHeaderForm({...headerForm, logoColorDark: e.target.value})} 
                            className="w-10 h-10 rounded cursor-pointer border"
                          />
                          <Input 
                            value={headerForm.logoColorDark || '#16a34a'} 
                            onChange={e => setHeaderForm({...headerForm, logoColorDark: e.target.value})} 
                            className="flex-1" 
                            placeholder="#16a34a"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Preview */}
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500 mb-3 flex items-center gap-2">
                      <Eye className="w-4 h-4" /> Aperçu du header en temps réel :
                    </p>
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                      <div className="px-4 py-3 flex items-center justify-between">
                        {/* Logo Preview */}
                        <div className="flex items-center gap-2">
                          {headerForm.logoType === 'custom' && headerForm.customLogoUrl ? (
                            <img 
                              src={headerForm.customLogoUrl} 
                              alt="Logo" 
                              className="w-8 h-8 object-contain"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          ) : headerForm.logoType !== 'none' && (
                            <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none">
                              <path d="M20 8c-2 0-3.5 1.5-3.5 3.5S18 15 20 15s3.5-1.5 3.5-3.5S22 8 20 8z" fill={headerForm.logoColor || '#22c55e'}/>
                              <path d="M12 12c-1.5 0-2.5 1-2.5 2.5s1 2.5 2.5 2.5 2.5-1 2.5-2.5-1-2.5-2.5-2.5z" fill={headerForm.logoColor || '#22c55e'}/>
                              <path d="M28 12c-1.5 0-2.5 1-2.5 2.5s1 2.5 2.5 2.5 2.5-1 2.5-2.5-1-2.5-2.5-2.5z" fill={headerForm.logoColor || '#22c55e'}/>
                              <path d="M20 18c-6 0-10 4-10 9 0 3 2 5 5 5 2 0 3.5-1 5-3 1.5 2 3 3 5 3 3 0 5-2 5-5 0-5-4-9-10-9z" fill={headerForm.logoColorDark || '#16a34a'}/>
                            </svg>
                          )}
                          <span className="text-lg font-bold">
                            <span className="text-gray-800">{headerForm.logoTextPart1 || 'VETO'}</span>
                            <span style={{ color: headerForm.logoColor || '#22c55e' }}>{headerForm.logoTextPart2 || 'RDV'}</span>
                          </span>
                        </div>
                        
                        {/* Nav Links Preview */}
                        <div className="flex items-center gap-4">
                          {(headerForm.navLinks || []).filter(l => l.active).slice(0, 4).map((link, i) => (
                            <span 
                              key={i} 
                              className={`text-sm ${link.highlight ? 'bg-blue-600 text-white px-3 py-1 rounded-full' : 'text-gray-600'}`}
                            >
                              {link.label}
                            </span>
                          ))}
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <Moon className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Les modifications sont appliquées en temps réel dans l'aperçu</p>
                  </div>
                </div>

                {/* Navigation Links */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium">Liens de navigation</h3>
                    <Button size="sm" onClick={handleAddNavLink} className="bg-green-500 hover:bg-green-600 text-white">
                      <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(headerForm.navLinks || []).map((link) => (
                      <div key={link.id} className={`flex items-center justify-between p-3 border rounded-lg ${!link.active ? 'opacity-50 bg-gray-50' : 'bg-white'}`}>
                        <div className="flex items-center gap-3">
                          <GripVertical className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="font-medium">{link.label}</p>
                            <p className="text-xs text-gray-500">{link.path} • Icône: {link.icon || 'aucune'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch checked={link.active} onCheckedChange={() => handleToggleNavLink(link.id)} />
                          <Button variant="ghost" size="sm" onClick={() => handleEditNavLink(link)}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteNavLink(link.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      </div>
                    ))}
                    {(!headerForm.navLinks || headerForm.navLinks.length === 0) && (
                      <p className="text-center text-gray-400 py-4">Aucun lien configuré</p>
                    )}
                  </div>
                </div>

                <Button onClick={handleSaveHeader} className="bg-green-500 hover:bg-green-600 text-white">
                  <Save className="w-4 h-4 mr-2" /> Enregistrer le Header
                </Button>
              </div>
            </div>

            {/* Footer Section */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <PanelBottom className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-semibold">Gestion du Footer</h2>
              </div>

              <div className="space-y-6">
                {/* General Footer Settings */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium mb-4">Paramètres généraux</h3>
                  <div className="grid gap-4">
                    <div>
                      <Label>Description</Label>
                      <Textarea 
                        value={footerForm.description || ''} 
                        onChange={e => setFooterForm({...footerForm, description: e.target.value})} 
                        className="mt-1" 
                        rows={2}
                        placeholder="Trouvez un vétérinaire et prenez rendez-vous..."
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Texte copyright</Label>
                        <Input 
                          value={footerForm.copyrightText || ''} 
                          onChange={e => setFooterForm({...footerForm, copyrightText: e.target.value})} 
                          className="mt-1" 
                          placeholder="vetoRDV® - Tous droits réservés"
                        />
                      </div>
                      <div>
                        <Label>Année de début</Label>
                        <Input 
                          value={footerForm.copyrightStartYear || ''} 
                          onChange={e => setFooterForm({...footerForm, copyrightStartYear: e.target.value})} 
                          className="mt-1" 
                          placeholder="2017"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Slogan</Label>
                      <Input 
                        value={footerForm.slogan || ''} 
                        onChange={e => setFooterForm({...footerForm, slogan: e.target.value})} 
                        className="mt-1" 
                        placeholder="Fait avec ❤️ pour les animaux"
                      />
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={footerForm.showSocialLinks !== false} 
                          onCheckedChange={checked => setFooterForm({...footerForm, showSocialLinks: checked})} 
                        />
                        <Label>Afficher les réseaux sociaux</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={footerForm.showAdminLink !== false} 
                          onCheckedChange={checked => setFooterForm({...footerForm, showAdminLink: checked})} 
                        />
                        <Label>Afficher lien admin</Label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Sections */}
                <div className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium">Sections du footer</h3>
                    <Button size="sm" onClick={handleAddFooterSection} className="bg-green-500 hover:bg-green-600 text-white">
                      <Plus className="w-4 h-4 mr-1" /> Nouvelle section
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {(footerForm.sections || []).map((section) => (
                      <div key={section.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-center mb-3">
                          <h4 className="font-semibold text-gray-800">{section.title}</h4>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleAddFooterLink(section.id)}>
                              <Plus className="w-3 h-3 mr-1" /> Lien
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEditFooterSection(section)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteFooterSection(section.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-1 pl-4">
                          {(section.links || []).map((link) => (
                            <div key={link.id} className="flex items-center justify-between py-1 text-sm">
                              <span className="text-gray-600">
                                {link.label} 
                                <span className="text-gray-400 ml-2">→ {link.path}</span>
                                {link.isExternal && <ExternalLink className="w-3 h-3 inline ml-1" />}
                              </span>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleEditFooterLink(section.id, link)}>
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteFooterLink(section.id, link.id)}>
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          {(!section.links || section.links.length === 0) && (
                            <p className="text-xs text-gray-400">Aucun lien</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {(!footerForm.sections || footerForm.sections.length === 0) && (
                      <p className="text-center text-gray-400 py-4">Aucune section configurée</p>
                    )}
                  </div>
                </div>

                <Button onClick={handleSaveFooter} className="bg-green-500 hover:bg-green-600 text-white">
                  <Save className="w-4 h-4 mr-2" /> Enregistrer le Footer
                </Button>
              </div>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
              <h3 className="font-semibold">Paramètres généraux</h3>
              <div className="grid gap-4">
                <div><Label>Nom du site</Label><Input value={settingsForm.siteName || ''} onChange={e => setSettingsForm({...settingsForm, siteName: e.target.value})} className="mt-1" /></div>
                <div><Label>Email</Label><Input value={settingsForm.contact?.email || ''} onChange={e => setSettingsForm({...settingsForm, contact: {...settingsForm.contact, email: e.target.value}})} className="mt-1" /></div>
                <div><Label>Téléphone</Label><Input value={settingsForm.contact?.phone || ''} onChange={e => setSettingsForm({...settingsForm, contact: {...settingsForm.contact, phone: e.target.value}})} className="mt-1" /></div>
              </div>
              <Button onClick={handleSaveSettings} className="bg-green-500 hover:bg-green-600 text-white"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
            </div>

            {/* Password Change Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Lock className="w-6 h-6 text-green-500" />
                <h3 className="font-semibold">Changer le mot de passe</h3>
              </div>
              <div className="grid gap-4 max-w-md">
                <div>
                  <Label>Mot de passe actuel</Label>
                  <Input 
                    type="password" 
                    value={passwordForm.currentPassword} 
                    onChange={e => setPasswordForm({...passwordForm, currentPassword: e.target.value})} 
                    className="mt-1" 
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <Label>Nouveau mot de passe</Label>
                  <Input 
                    type="password" 
                    value={passwordForm.newPassword} 
                    onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})} 
                    className="mt-1" 
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <Label>Confirmer le nouveau mot de passe</Label>
                  <Input 
                    type="password" 
                    value={passwordForm.confirmPassword} 
                    onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})} 
                    className="mt-1" 
                    placeholder="••••••••"
                  />
                </div>
              </div>
              <Button 
                onClick={handleChangePassword} 
                disabled={isChangingPassword || !passwordForm.currentPassword || !passwordForm.newPassword}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <Lock className="w-4 h-4 mr-2" />
                {isChangingPassword ? 'Modification...' : 'Modifier le mot de passe'}
              </Button>
            </div>
          </div>
        );

      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-gray-900 text-white transition-all duration-300 z-50 flex flex-col ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="p-4 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center"><Shield className="w-5 h-5" /></div>
            {sidebarOpen && <div><h1 className="font-bold">vetoRDV</h1><p className="text-xs text-gray-400">Administration</p></div>}
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {menuItems.map((item) => (
            <button key={item.id} onClick={() => setActiveTab(item.id)} className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${activeTab === item.id ? 'bg-green-500 text-white' : 'text-gray-400 hover:bg-gray-800'}`}>
              <item.icon className="w-5 h-5" />{sidebarOpen && <span className="text-sm">{item.label}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-800 flex-shrink-0">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-400 hover:bg-gray-800"><LogOut className="w-5 h-5" />{sidebarOpen && <span className="text-sm">Déconnexion</span>}</button>
        </div>
      </aside>

      {/* Main */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg hover:bg-gray-100"><Menu className="w-5 h-5" /></button>
              <h1 className="text-xl font-semibold capitalize">{menuItems.find(i => i.id === activeTab)?.label}</h1>
            </div>
            <div className="flex items-center gap-4">
              {/* Notification Bell */}
              <div className="relative">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)} 
                  className="p-2 rounded-lg hover:bg-gray-100 relative"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-lg border z-50 max-h-96 overflow-hidden">
                    <div className="p-4 border-b flex justify-between items-center">
                      <h3 className="font-semibold">Notifications</h3>
                      {unreadCount > 0 && (
                        <button 
                          onClick={markAllNotificationsRead}
                          className="text-sm text-green-600 hover:underline"
                        >
                          Tout marquer comme lu
                        </button>
                      )}
                    </div>
                    <div className="overflow-y-auto max-h-72">
                      {notifications.length > 0 ? notifications.slice(0, 10).map((notif) => (
                        <div 
                          key={notif.notification_id} 
                          className={`p-4 border-b hover:bg-gray-50 cursor-pointer ${!notif.is_read ? 'bg-green-50' : ''}`}
                          onClick={() => {
                            if (!notif.is_read) markNotificationRead(notif.notification_id);
                            if (notif.type === 'new_vet_registration') {
                              setActiveTab('veterinaires');
                            } else if (notif.type === 'new_patient_registration') {
                              setActiveTab('patients');
                            }
                            setShowNotifications(false);
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              notif.type === 'new_vet_registration' ? 'bg-blue-100' : 'bg-green-100'
                            }`}>
                              {notif.type === 'new_vet_registration' ? (
                                <Stethoscope className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Users className="w-5 h-5 text-green-600" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{notif.title}</p>
                              <p className="text-xs text-gray-600 mt-1">{notif.message}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(notif.created_at).toLocaleDateString('fr-FR', { 
                                  day: 'numeric', 
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            {!notif.is_read && (
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            )}
                          </div>
                        </div>
                      )) : (
                        <div className="p-8 text-center text-gray-500">
                          <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>Aucune notification</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center"><span className="text-white text-sm font-medium">A</span></div>
            </div>
          </div>
        </header>
        <main className="p-6">{renderContent()}</main>
      </div>

      {/* Modals */}
      <ArticleEditor article={editingArticle} isOpen={isEditorOpen} onClose={() => setIsEditorOpen(false)} onSave={handleSaveArticle} />

      {/* FAQ Editor */}
      <Dialog open={isFAQEditorOpen} onOpenChange={setIsFAQEditorOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingFAQ ? 'Modifier FAQ' : 'Nouvelle FAQ'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Question</Label><Input value={faqForm.question} onChange={e => setFaqForm({...faqForm, question: e.target.value})} className="mt-1" /></div>
            <div><Label>Réponse</Label><Textarea value={faqForm.answer} onChange={e => setFaqForm({...faqForm, answer: e.target.value})} rows={4} className="mt-1" /></div>
            <div className="flex gap-3"><Button variant="outline" onClick={() => setIsFAQEditorOpen(false)} className="flex-1">Annuler</Button><Button onClick={handleSaveFAQ} className="flex-1 bg-green-500 text-white">Enregistrer</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ad Editor */}
      <Dialog open={isAdEditorOpen} onOpenChange={setIsAdEditorOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingAd ? 'Modifier la publicité' : 'Nouvelle publicité'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Nom *</Label><Input value={adForm.name} onChange={e => setAdForm({...adForm, name: e.target.value})} className="mt-1" placeholder="Bannière Royal Canin" /></div>
            <div><Label>Annonceur</Label><Input value={adForm.advertiser} onChange={e => setAdForm({...adForm, advertiser: e.target.value})} className="mt-1" placeholder="Royal Canin" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Position</Label>
                <select value={adForm.position} onChange={e => setAdForm({...adForm, position: e.target.value})} className="mt-1 w-full border rounded-md p-2">
                  <option value="header">Header</option>
                  <option value="sidebar">Sidebar</option>
                  <option value="article-middle">Milieu article</option>
                  <option value="article-end">Fin article</option>
                </select>
              </div>
              <div><Label>Prix (€)</Label><Input type="number" value={adForm.price} onChange={e => setAdForm({...adForm, price: parseFloat(e.target.value)})} className="mt-1" /></div>
            </div>
            <div><Label>URL Image *</Label><Input value={adForm.imageUrl} onChange={e => setAdForm({...adForm, imageUrl: e.target.value})} className="mt-1" placeholder="https://..." /></div>
            {adForm.imageUrl && <img src={adForm.imageUrl} alt="Preview" className="w-full h-32 object-cover rounded-lg" />}
            <div><Label>URL de destination *</Label><Input value={adForm.linkUrl} onChange={e => setAdForm({...adForm, linkUrl: e.target.value})} className="mt-1" placeholder="https://..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Date début</Label><Input type="date" value={adForm.startDate} onChange={e => setAdForm({...adForm, startDate: e.target.value})} className="mt-1" /></div>
              <div><Label>Date fin</Label><Input type="date" value={adForm.endDate} onChange={e => setAdForm({...adForm, endDate: e.target.value})} className="mt-1" /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={adForm.active} onCheckedChange={checked => setAdForm({...adForm, active: checked})} /><Label>Active</Label></div>
            <div className="flex gap-3"><Button variant="outline" onClick={() => setIsAdEditorOpen(false)} className="flex-1">Annuler</Button><Button onClick={handleSaveAd} className="flex-1 bg-green-500 text-white">Enregistrer</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Editor */}
      <Dialog open={isProductEditorOpen} onOpenChange={setIsProductEditorOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingProduct ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Nom du produit *</Label><Input value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} className="mt-1" /></div>
            <div><Label>Marque</Label><Input value={productForm.brand} onChange={e => setProductForm({...productForm, brand: e.target.value})} className="mt-1" /></div>
            <div><Label>Description</Label><Textarea value={productForm.description} onChange={e => setProductForm({...productForm, description: e.target.value})} rows={3} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Prix (€) *</Label><Input type="number" value={productForm.price} onChange={e => setProductForm({...productForm, price: parseFloat(e.target.value)})} className="mt-1" /></div>
              <div><Label>Prix original (€)</Label><Input type="number" value={productForm.originalPrice} onChange={e => setProductForm({...productForm, originalPrice: parseFloat(e.target.value)})} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Catégorie</Label>
                <select value={productForm.category} onChange={e => setProductForm({...productForm, category: e.target.value})} className="mt-1 w-full border rounded-md p-2">
                  {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div><Label>Animal</Label>
                <select value={productForm.animal} onChange={e => setProductForm({...productForm, animal: e.target.value})} className="mt-1 w-full border rounded-md p-2">
                  <option value="tous">Tous</option>
                  <option value="chien">Chien</option>
                  <option value="chat">Chat</option>
                  <option value="nac">NAC</option>
                </select>
              </div>
            </div>
            <div><Label>URL Image *</Label><Input value={productForm.image} onChange={e => setProductForm({...productForm, image: e.target.value})} className="mt-1" /></div>
            {productForm.image && <img src={productForm.image} alt="Preview" className="w-full h-32 object-cover rounded-lg" />}
            <div><Label>Lien affilié *</Label><Input value={productForm.affiliateUrl} onChange={e => setProductForm({...productForm, affiliateUrl: e.target.value})} className="mt-1" placeholder="https://amazon.fr/..." /></div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2"><Switch checked={productForm.active} onCheckedChange={checked => setProductForm({...productForm, active: checked})} /><Label>Actif</Label></div>
              <div className="flex items-center gap-2"><Switch checked={productForm.featured} onCheckedChange={checked => setProductForm({...productForm, featured: checked})} /><Label>Vedette</Label></div>
            </div>
            <div className="flex gap-3"><Button variant="outline" onClick={() => setIsProductEditorOpen(false)} className="flex-1">Annuler</Button><Button onClick={handleSaveProduct} className="flex-1 bg-green-500 text-white">Enregistrer</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-red-600">Confirmer la suppression</DialogTitle><DialogDescription>Cette action est irréversible.</DialogDescription></DialogHeader>
          <div className="flex gap-3 mt-4"><Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} className="flex-1">Annuler</Button><Button onClick={handleConfirmDelete} className="flex-1 bg-red-500 text-white">Supprimer</Button></div>
        </DialogContent>
      </Dialog>

      {/* Nav Link Editor */}
      <Dialog open={isNavLinkEditorOpen} onOpenChange={setIsNavLinkEditorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingNavLink ? 'Modifier le lien' : 'Nouveau lien de navigation'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Libellé *</Label>
              <Input 
                value={navLinkForm.label} 
                onChange={e => setNavLinkForm({...navLinkForm, label: e.target.value})} 
                className="mt-1" 
                placeholder="Conseils"
              />
            </div>
            <div>
              <Label>Chemin (URL) *</Label>
              <Input 
                value={navLinkForm.path} 
                onChange={e => setNavLinkForm({...navLinkForm, path: e.target.value})} 
                className="mt-1" 
                placeholder="/conseils"
              />
            </div>
            <div>
              <Label>Icône</Label>
              <select 
                value={navLinkForm.icon || 'none'} 
                onChange={e => setNavLinkForm({...navLinkForm, icon: e.target.value})} 
                className="mt-1 w-full border rounded-md p-2"
              >
                <option value="none">Aucune</option>
                <option value="user">Utilisateur</option>
                <option value="stethoscope">Stéthoscope</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={navLinkForm.active} 
                onCheckedChange={checked => setNavLinkForm({...navLinkForm, active: checked})} 
              />
              <Label>Actif</Label>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setIsNavLinkEditorOpen(false)} className="flex-1">Annuler</Button>
              <Button onClick={handleSaveNavLink} className="flex-1 bg-green-500 text-white">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer Section Editor */}
      <Dialog open={isFooterSectionEditorOpen} onOpenChange={setIsFooterSectionEditorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFooterSection ? 'Modifier la section' : 'Nouvelle section'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Titre de la section *</Label>
              <Input 
                value={footerSectionForm.title} 
                onChange={e => setFooterSectionForm({...footerSectionForm, title: e.target.value})} 
                className="mt-1" 
                placeholder="Navigation"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setIsFooterSectionEditorOpen(false)} className="flex-1">Annuler</Button>
              <Button onClick={handleSaveFooterSection} className="flex-1 bg-green-500 text-white">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer Link Editor */}
      <Dialog open={isFooterLinkEditorOpen} onOpenChange={setIsFooterLinkEditorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFooterLink?.link ? 'Modifier le lien' : 'Nouveau lien'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Libellé *</Label>
              <Input 
                value={footerLinkForm.label} 
                onChange={e => setFooterLinkForm({...footerLinkForm, label: e.target.value})} 
                className="mt-1" 
                placeholder="Contact"
              />
            </div>
            <div>
              <Label>Chemin/URL *</Label>
              <Input 
                value={footerLinkForm.path} 
                onChange={e => setFooterLinkForm({...footerLinkForm, path: e.target.value})} 
                className="mt-1" 
                placeholder="/contact ou https://..."
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={footerLinkForm.isExternal} 
                  onCheckedChange={checked => setFooterLinkForm({...footerLinkForm, isExternal: checked, isEmail: false, isPhone: false})} 
                />
                <Label>Lien externe</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={footerLinkForm.isEmail} 
                  onCheckedChange={checked => setFooterLinkForm({...footerLinkForm, isEmail: checked, isExternal: false, isPhone: false})} 
                />
                <Label>Email</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={footerLinkForm.isPhone} 
                  onCheckedChange={checked => setFooterLinkForm({...footerLinkForm, isPhone: checked, isExternal: false, isEmail: false})} 
                />
                <Label>Téléphone</Label>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setIsFooterLinkEditorOpen(false)} className="flex-1">Annuler</Button>
              <Button onClick={handleSaveFooterLink} className="flex-1 bg-green-500 text-white">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Details Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {profileType === 'vet' ? (
                <><Stethoscope className="w-5 h-5 text-blue-600" /> Fiche Vétérinaire</>
              ) : (
                <><Users className="w-5 h-5 text-green-600" /> Fiche Patient</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {loadingProfile ? (
            <div className="py-8 text-center text-gray-500">Chargement...</div>
          ) : selectedProfile ? (
            <div className="space-y-6 py-4">
              {/* User Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Informations du compte
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Nom:</span>
                    <p className="font-medium">{selectedProfile.user?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <p className="font-medium">{selectedProfile.user?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Statut:</span>
                    <p className={`font-medium ${
                      selectedProfile.user?.status === 'approved' ? 'text-green-600' :
                      selectedProfile.user?.status === 'rejected' ? 'text-red-600' :
                      'text-amber-600'
                    }`}>
                      {selectedProfile.user?.status === 'approved' ? 'Approuvé' :
                       selectedProfile.user?.status === 'rejected' ? 'Refusé' : 'En attente'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Date d'inscription:</span>
                    <p className="font-medium">
                      {selectedProfile.user?.created_at 
                        ? new Date(selectedProfile.user.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Profile Info */}
              {selectedProfile.profile && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    {profileType === 'vet' ? (
                      <><Stethoscope className="w-4 h-4" /> Informations du cabinet</>
                    ) : (
                      <><Users className="w-4 h-4" /> Informations du profil</>
                    )}
                  </h3>
                  
                  {profileType === 'vet' ? (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Nom du cabinet:</span>
                        <p className="font-medium">{selectedProfile.profile.clinic_name || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Téléphone:</span>
                        <p className="font-medium">{selectedProfile.profile.phone || 'N/A'}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Adresse:</span>
                        <p className="font-medium">
                          {selectedProfile.profile.address || 'N/A'}, {selectedProfile.profile.postal_code || ''} {selectedProfile.profile.city || ''}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Plan choisi:</span>
                        <p className="font-medium capitalize">{selectedProfile.profile.selected_plan || 'Pro'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Statut abonnement:</span>
                        <p className={`font-medium ${
                          selectedProfile.profile.subscription_status === 'active' ? 'text-green-600' :
                          selectedProfile.profile.subscription_status === 'trial' ? 'text-blue-600' :
                          'text-gray-600'
                        }`}>
                          {selectedProfile.profile.subscription_status === 'active' ? 'Actif' :
                           selectedProfile.profile.subscription_status === 'trial' ? 'Essai gratuit' : 'Inactif'}
                        </p>
                      </div>
                      {selectedProfile.profile.specialties?.length > 0 && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Spécialités:</span>
                          <p className="font-medium">{selectedProfile.profile.specialties.join(', ')}</p>
                        </div>
                      )}
                      {selectedProfile.profile.description && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Description:</span>
                          <p className="font-medium">{selectedProfile.profile.description}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Téléphone:</span>
                        <p className="font-medium">{selectedProfile.profile.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Ville:</span>
                        <p className="font-medium">{selectedProfile.profile.city || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Animaux:</span>
                        <p className="font-medium">{selectedProfile.profile.animals?.length || 0} animal(aux)</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Rendez-vous:</span>
                        <p className="font-medium">{selectedProfile.appointments_count || 0} RDV</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              {selectedProfile.user?.status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button 
                    onClick={() => {
                      if (profileType === 'vet') {
                        approveVeterinarian(selectedProfile.user.user_id);
                      } else {
                        approvePatient(selectedProfile.user.user_id);
                      }
                      setShowProfileModal(false);
                    }} 
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Approuver
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      if (profileType === 'vet') {
                        rejectVeterinarian(selectedProfile.user.user_id);
                      } else {
                        rejectPatient(selectedProfile.user.user_id);
                      }
                      setShowProfileModal(false);
                    }} 
                    className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <XOctagon className="w-4 h-4 mr-2" /> Refuser
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">Aucune donnée disponible</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
